(function bootstrapManageShipment() {
    'use strict';

    // Prevent double initialization
    if (window.__ET_MANAGE_SHIPMENT_LOADED__) {
        return;
    }
    window.__ET_MANAGE_SHIPMENT_LOADED__ = true;

    function getSharedRechargeSdk() {
        if (!window.etRechargeSdk) {
            throw new Error(
                'Shared Recharge SDK is unavailable. Ensure recharge-sdk-bootstrap.js loads before affinity-manage-shipment.js.'
            );
        }
        const sharedSdk = window.etRechargeSdk;
        if (typeof sharedSdk.getMethod !== 'function') {
            throw new Error(
                'Shared Recharge SDK is not properly initialized. The getMethod function is not available.'
            );
        }
        return sharedSdk;
    }

    function getRechargeMethod(pathSegments) {
        return getSharedRechargeSdk().getMethod(pathSegments);
    }

    function getOptionalRechargeMethod(pathSegments) {
        try {
            return getRechargeMethod(pathSegments);
        } catch (error) {
            return null;
        }
    }

    function loginCustomerPortal(...args) {
        return getRechargeMethod(['auth', 'loginCustomerPortal'])(...args);
    }

    // Reads share one per-page-view memo with every other portal script, so
    // opening this modal from the overview reuses what the overview already
    // fetched (#341).
    function rechargeRead(pathSegments, session, ...args) {
        const sharedRead = window.etRechargeUtils?.memoizedRechargeRead;
        if (typeof sharedRead === 'function') {
            return sharedRead(pathSegments, session, args);
        }
        return getRechargeMethod(pathSegments)(session, ...args);
    }

    // Writes invalidate that memo before the caller's `await` resumes.
    function rechargeWrite(pathSegments, ...args) {
        const sharedWrite = window.etRechargeUtils?.runRechargeWrite;
        if (typeof sharedWrite === 'function') {
            return sharedWrite(pathSegments, ...args);
        }
        return getRechargeMethod(pathSegments)(...args);
    }

    function listSubscriptions(...args) {
        return rechargeRead(['subscription', 'listSubscriptions'], ...args);
    }

    function getSubscription(...args) {
        return rechargeRead(['subscription', 'getSubscription'], ...args);
    }

    function getAddress(...args) {
        return rechargeRead(['address', 'getAddress'], ...args);
    }

    function listAddresses(...args) {
        return rechargeRead(['address', 'listAddresses'], ...args);
    }

    function getBundleSelection(...args) {
        return rechargeRead(['bundle', 'getBundleSelection'], ...args);
    }

    function listBundleSelections(...args) {
        return rechargeRead(['bundle', 'listBundleSelections'], ...args);
    }

    function productSearch(...args) {
        return rechargeRead(['product', 'productSearch'], ...args);
    }

    function updateBundleSelection(...args) {
        return rechargeWrite(['bundle', 'updateBundleSelection'], ...args);
    }

    function updateAddress(...args) {
        return rechargeWrite(['address', 'updateAddress'], ...args);
    }

    function resolvePortalTimeZone() {
        const configuredTimeZone = String(
            window.etAffinityRescheduleConfig?.timeZone || ''
        ).trim();
        if (configuredTimeZone) return configuredTimeZone;

        return 'America/Los_Angeles';
    }

    const CONFIG = Object.freeze({
        timeZone: resolvePortalTimeZone(),
    });

    const state = {
        sdkInitialized: false,
        sessionPromise: null,
        modalOpen: false,
        modalType: null,
        // Portal-timings handle: Manage Delivery click -> "Loading times..."
        // replaced (#347).
        deliveryOpenSpan: null,
        saveInProgress: false,
        loaderOverlay: null,
    };

    const LOADER_OVERLAY_ID = 'et-shipment-loader-overlay';

    const LOADER_OVERLAY_HTML = `
      <div id="${LOADER_OVERLAY_ID}" data-et-shipment-loader="true" class="et-shipment-loader-overlay">
        <div class="et-shipment-loader-spinner" aria-live="polite" aria-busy="true"></div>
        <span class="et-shipment-loader-text">Loading...</span>
      </div>`.trim();

    function showLoaderOverlay() {
        hideLoaderOverlay();
        const wrap = document.createElement('div');
        wrap.innerHTML = LOADER_OVERLAY_HTML;
        const overlay = wrap.firstElementChild;
        document.body.appendChild(overlay);
        state.loaderOverlay = overlay;
    }

    function hideLoaderOverlay() {
        const existing = document.getElementById(LOADER_OVERLAY_ID) || document.querySelector('[data-et-shipment-loader="true"]');
        if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }
        state.loaderOverlay = null;
    }

    function parseInteger(rawValue, fallback = 0) {
        const parsed = Number.parseInt(String(rawValue), 10);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    function parseOptionalSubscriptionId(rawValue) {
        if (rawValue === null || typeof rawValue === 'undefined') return null;
        const parsed = parseInteger(rawValue, Number.NaN);
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        return parsed;
    }

    function normalizeCustomAttributeKey(rawValue) {
        return String(rawValue || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
    }

    function normalizeTimeInputValue(rawValue) {
        const raw = String(rawValue || '').trim();
        if (!raw) return '';
        const normalized = raw.replace(/\s+/g, ' ').toUpperCase();
        const match = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/);
        if (!match) return '';
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3];
        if (period === 'PM' && hours < 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    function normalizeQuarterHourTime(rawValue) {
        const raw = String(rawValue || '').trim();
        if (!raw) return '';
        const parsed = normalizeTimeInputValue(raw);
        if (!parsed) return '';
        const [hours, minutes] = parsed.split(':').map((v) => parseInt(v, 10));
        const roundedMinutes = Math.round(minutes / 15) * 15;
        const totalMinutes = hours * 60 + roundedMinutes;
        const finalHours = Math.floor(totalMinutes / 60) % 24;
        const finalMinutes = totalMinutes % 60;
        return `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
    }

    function extractTimeFromFulfillmentDate(rawValue) {
        const raw = String(rawValue || '').trim();
        if (!raw) return '';

        // ISO date-time with optional timezone, e.g. "2026-02-24T08:45:00-08:00"
        const isoMatch = raw.match(/T(\d{2}):(\d{2}):\d{2}/);
        if (isoMatch) {
            return `${isoMatch[1]}:${isoMatch[2]}`;
        }

        // 12-hour time with AM/PM, e.g. "8:45 AM" or "2:00 PM"
        const plain12h = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])$/);
        if (plain12h) {
            let hours = parseInt(plain12h[1], 10);
            const minutes = parseInt(plain12h[2], 10);
            const ampm = plain12h[3].toUpperCase();
            if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
            // Convert to 24-hour format
            if (ampm === 'PM' && hours < 12) hours += 12;
            if (ampm === 'AM' && hours === 12) hours = 0;
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        // 24-hour time only, e.g. "08:45" or "14:30"
        const plain24h = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (plain24h) {
            const hours = parseInt(plain24h[1], 10);
            const minutes = parseInt(plain24h[2], 10);
            if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return '';
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        return '';
    }

    function readNamedAttributeValue(attributes, targetKey) {
        const normalizedTargetKey = normalizeCustomAttributeKey(targetKey);
        if (!normalizedTargetKey || !attributes) return null;

        if (Array.isArray(attributes)) {
            for (const entry of attributes) {
                // Handle array format: [key, value]
                if (Array.isArray(entry) && entry.length >= 2) {
                    const key = normalizeCustomAttributeKey(entry[0]);
                    if (key === normalizedTargetKey) {
                        return String(entry[1] || '').trim() || null;
                    }
                    continue;
                }

                // Handle object format
                if (!entry || typeof entry !== 'object') continue;

                const key = normalizeCustomAttributeKey(
                    entry.key || entry.name || entry.first || entry.attribute || ''
                );
                if (key !== normalizedTargetKey) continue;

                const value = String(
                    entry.value ?? entry.last ?? entry.val ?? entry.text ?? ''
                );
                return value.trim() || null;
            }
        }

        return null;
    }

    function extractExistingDeliveryTime(subscription, address) {
        const addressAttributes = address?.order_attributes || address?.orderAttributes || address?.attributes;
        const fulfillmentDateFromAddress = readNamedAttributeValue(addressAttributes, 'Fulfillment Date');
        if (fulfillmentDateFromAddress) {
            const time = extractTimeFromFulfillmentDate(fulfillmentDateFromAddress);
            if (time) return time;
        }

        return '';
    }

    function formatDateInputValue(dateValue) {
        if (!(dateValue instanceof Date) || !Number.isFinite(dateValue.getTime())) {
            return '';
        }

        const year = dateValue.getFullYear();
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function parseIsoDateParts(datePart) {
        const match = String(datePart || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;
        return {
            year: Number.parseInt(match[1], 10),
            month: Number.parseInt(match[2], 10),
            day: Number.parseInt(match[3], 10),
        };
    }

    function parseTimeComponents(timeValue) {
        const match = String(timeValue || '').match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;
        return {
            hours: Number.parseInt(match[1], 10),
            minutes: Number.parseInt(match[2], 10),
        };
    }

    function composeFulfillmentDateTimeString(datePart, normalizedTime, existingFulfillmentDate) {
        const existing = String(existingFulfillmentDate || '').trim();

        if (normalizedTime && /T\d{2}:\d{2}/.test(existing)) {
            const parsedTimeForSplice = parseTimeComponents(normalizedTime);
            if (parsedTimeForSplice) {
                const hourValue = String(parsedTimeForSplice.hours).padStart(2, '0');
                const minuteValue = String(parsedTimeForSplice.minutes).padStart(2, '0');
                // Time-only: replace the first `T…` clock segment; date and suffix stay unchanged.
                // Example: input  "2026-03-24T14:30:00-08:00" → output "2026-03-24T15:00:00-08:00" (15:00 selected)
                // Example: input  "2026-03-24T14:30:00Z" → output "2026-03-24T15:00:00Z"
                return existing.replace(
                    /T\d{2}:\d{2}(?::\d{2})?/,
                    `T${hourValue}:${minuteValue}:00`
                );
            }
        }

        const parsedTime = parseTimeComponents(normalizedTime);
        if (!parsedTime) {
            return `${datePart}T${normalizedTime}:00`;
        }
        const hourValue = String(parsedTime.hours).padStart(2, '0');
        const minuteValue = String(parsedTime.minutes).padStart(2, '0');
        return `${datePart}T${hourValue}:${minuteValue}:00`;
    }

    function buildFulfillmentDateValue(deliveryDatePart, existingValue, deliveryTime) {
        const existing = String(existingValue || '').trim();
        const normalizedTime = normalizeQuarterHourTime(deliveryTime);

        if (normalizedTime) {
            return composeFulfillmentDateTimeString(deliveryDatePart, normalizedTime, existing);
        }

        // If no time but existing value has a T part, preserve the existing suffix
        const match = existing.match(/^(\d{4}-\d{2}-\d{2})(.*)$/);
        if (match) {
            const suffix = String(match[2] || '');
            return `${deliveryDatePart}${suffix}`;
        }

        return deliveryDatePart;
    }

    function extractIsoDatePart(value) {
        const match = String(value || '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
        return match ? match[1] : '';
    }

    function addDaysToIsoDatePart(datePart, dayOffset) {
        const parsedDate = parseIsoDateParts(datePart);
        if (!parsedDate) return '';

        const utcTimestamp = Date.UTC(parsedDate.year, parsedDate.month - 1, parsedDate.day);
        const shiftedDate = new Date(utcTimestamp + dayOffset * 24 * 60 * 60 * 1000);
        const year = shiftedDate.getUTCFullYear();
        const month = String(shiftedDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(shiftedDate.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function resolveCurrentDatePartForTimeZone(timeZone) {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());

        const year = parts.find((part) => part.type === 'year')?.value;
        const month = parts.find((part) => part.type === 'month')?.value;
        const day = parts.find((part) => part.type === 'day')?.value;
        if (!year || !month || !day) return '';
        return `${year}-${month}-${day}`;
    }

    function resolveFallbackFulfillmentDatePart(subscription) {
        const chargeDatePart = extractIsoDatePart(subscription?.next_charge_scheduled_at || '');
        if (chargeDatePart) {
            return addDaysToIsoDatePart(chargeDatePart, 2);
        }

        const zonedToday = resolveCurrentDatePartForTimeZone(CONFIG.timeZone);
        if (zonedToday) {
            return addDaysToIsoDatePart(zonedToday, 2);
        }

        return formatDateInputValue(new Date());
    }

    function generateDeliveryTimeSlots(openingTime, closingTime) {
        const slots = [];
        const normalizedOpen = normalizeQuarterHourTime(openingTime);
        const normalizedClose = normalizeQuarterHourTime(closingTime);

        if (!normalizedOpen || !normalizedClose) {
            return slots;
        }

        const [openHours, openMinutes] = normalizedOpen.split(':').map(v => parseInt(v, 10));
        const [closeHours, closeMinutes] = normalizedClose.split(':').map(v => parseInt(v, 10));

        // Start 45 minutes after opening
        let startMinutes = openHours * 60 + openMinutes + 45;
        const endMinutes = closeHours * 60 + closeMinutes;

        // Generate 15-minute interval slots
        for (let minutes = startMinutes; minutes <= endMinutes; minutes += 15) {
            const hours = Math.floor(minutes / 60) % 24;
            const mins = minutes % 60;
            const value = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;

            // Format label as 12-hour format with AM/PM
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            const displayMins = String(mins).padStart(2, '0');
            const label = `${displayHours}:${displayMins} ${period}`;

            slots.push({ value, label });
        }

        return slots;
    }

    function formatTimeSlotOptions(slots, selectedValue = '') {
        if (!slots.length) {
            return '<option value="">Select a time</option>';
        }

        let html = '<option value="">Select a time</option>';
        for (const slot of slots) {
            const selected = slot.value === selectedValue ? ' selected' : '';
            html += `<option value="${slot.value}"${selected}>${slot.label}</option>`;
        }
        return html;
    }

    function extractDeliveryLocationIdFromOrderAttributes(orderAttributes) {
        if (!Array.isArray(orderAttributes)) return null;
        for (const attr of orderAttributes) {
            if (!attr || typeof attr !== 'object') continue;
            let key = null;
            let value = null;
            if ('name' in attr && 'value' in attr) {
                key = String(attr.name || '').trim();
                value = attr.value;
            } else {
                const keys = Object.keys(attr);
                if (keys.length > 0) {
                    key = keys[0];
                    value = attr[key];
                }
            }
            if (key) {
                const normalizedKey = normalizeCustomAttributeKey(key);
                if (normalizedKey === 'locationid') {
                    return value != null ? String(value).trim() : null;
                }
            }
        }
        return null;
    }

    function escapeHtml(value) {
        return String(value)
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }

    // Regex: address fields - letters, numbers, spaces, and common punctuation
    const RE_ADDRESS = /^[a-zA-Z0-9\s.'"#\/&(),-]*$/;
    // Regex: 5-digit US ZIP
    const RE_ZIP = /^\d{5}$/;

    // The check could not run at all, so the delivery radius is not the answer.
    const DELIVERABILITY_UNAVAILABLE_MESSAGE =
        'We could not check delivery for this address right now. Please try again.';

    const OUT_OF_DELIVERY_AREA_MESSAGE =
        'Sorry, this address is outside our delivery area. Pickup is still available at any of our locations.';

    // Codes whose server copy we show verbatim: the address itself is the problem
    // (`unverifiable_address`), or the courier quote never ran (`quote_failed`,
    // e.g. a Nash outage). Neither means the subscriber is out of range, so
    // sending them to pickup would be wrong.
    const SERVER_COPY_DELIVERABILITY_CODES = ['unverifiable_address', 'quote_failed'];
    // Codes that really do mean "no courier can serve this route" -- what the
    // local out-of-area copy describes.
    const OUT_OF_AREA_DELIVERABILITY_CODES = ['no_courier', 'address_undeliverable'];

    // `console` is not wired to Sentry in this theme, so a bare console.error is
    // visible to the subscriber's devtools and to nobody on our side.
    // `etObservability` is installed by et-sentry.js (a no-op facade for
    // bot/disabled sessions), so the guarded call is safe at any load order.
    // `detail` may carry server text, so it goes to the console only -- the
    // Sentry message stays limited to the code and status.
    function reportDeliverabilityAnomaly(summary, detail) {
        console.error('[manage-shipment] ' + summary, detail);
        if (window.etObservability && typeof window.etObservability.captureMessage === 'function') {
            window.etObservability.captureMessage('[manage-shipment] ' + summary, 'warning');
        }
    }

    function getDeliverabilityBlockMessage(code, serverMessage) {
        if (SERVER_COPY_DELIVERABILITY_CODES.includes(code)) {
            const serverCopy = typeof serverMessage === 'string' ? serverMessage.trim() : '';
            if (serverCopy) return serverCopy;
            // These codes carry the whole explanation in `error`, so an empty one
            // leaves us nothing to say -- and falling through to the out-of-area
            // copy would re-create the exact #299 symptom for a failure that is
            // not about the radius. Ask for a retry, and report: the server owes
            // us a message here.
            reportDeliverabilityAnomaly('Deliverability code missing its message: ' + code, serverMessage);
            return DELIVERABILITY_UNAVAILABLE_MESSAGE;
        }

        if (code && !OUT_OF_AREA_DELIVERABILITY_CODES.includes(code)) {
            // The server grew a code this theme has not been taught. Keep the
            // fail-safe copy: `error` is only guaranteed shopper-safe on the Nash
            // branch, so showing it for any unknown code risks surfacing raw
            // courier SDK text. But do report it -- otherwise drift silently reads
            // as "outside our delivery area" for something that may not be. A
            // missing `code` is the pre-#395 server, not drift, so it stays quiet.
            reportDeliverabilityAnomaly('Unrecognized deliverability code: ' + code, serverMessage);
        }

        return OUT_OF_DELIVERY_AREA_MESSAGE;
    }

    function validateDeliveryField(fieldName, value) {
        const v = String(value ?? '').trim();
        switch (fieldName) {
            case 'address1':
                if (!v) return { valid: false, message: 'Address Line 1 is required.' };
                if (!RE_ADDRESS.test(v)) return {
                    valid: false,
                    message:
                        'Address can only contain letters, numbers, spaces, and . - \' " # / & , ( ).',
                };
                return { valid: true, message: '' };
            case 'address2':
                if (!RE_ADDRESS.test(v)) return {
                    valid: false,
                    message:
                        'Address can only contain letters, numbers, spaces, and . - \' " # / & , ( ).',
                };
                return { valid: true, message: '' };
            case 'city':
                if (!v) return { valid: false, message: 'City is required.' };
                if (!RE_ADDRESS.test(v)) return {
                    valid: false,
                    message:
                        'City can only contain letters, numbers, spaces, and . - \' " # / & , ( ).',
                };
                return { valid: true, message: '' };
            case 'state':
                if (!v) return { valid: false, message: 'State is required.' };
                if (!RE_ADDRESS.test(v)) return {
                    valid: false,
                    message:
                        'State can only contain letters, numbers, spaces, and . - \' " # / & , ( ).',
                };
                return { valid: true, message: '' };
            case 'zip':
                if (!v) return { valid: false, message: 'ZIP code is required.' };
                if (!RE_ZIP.test(v)) return { valid: false, message: 'Enter a valid 5-digit ZIP code.' };
                return { valid: true, message: '' };
            case 'deliveryTime':
                if (!v) return { valid: false, message: 'Preferred delivery time is required.' };
                return { valid: true, message: '' };
            case 'deliveryInstructions':
                return { valid: true, message: '' };
            default:
                return { valid: true, message: '' };
        }
    }

    function parseAddressApiError(errorData) {
        const fieldErrors = {};
        const apiToForm = { province: 'state' };
        const formFields = ['address1', 'address2', 'city', 'state', 'zip'];

        function mapToFormField(apiKey) {
            const k = String(apiKey || '').toLowerCase();
            return apiToForm[k] || (k === 'province' ? 'state' : apiKey);
        }

        function firstMessage(val) {
            if (val == null) return '';
            if (Array.isArray(val)) {
                const s = val.find((v) => v != null && String(v).trim());
                return s != null ? String(s).trim() : '';
            }
            return String(val).trim();
        }

        if (errorData && typeof errorData.errors === 'object' && !Array.isArray(errorData.errors)) {
            for (const [apiKey, value] of Object.entries(errorData.errors)) {
                const msg = firstMessage(value);
                if (!msg) continue;
                const formField = mapToFormField(apiKey);
                if (formFields.indexOf(formField) !== -1) {
                    fieldErrors[formField] = msg;
                }
            }
        }
        if (Object.keys(fieldErrors).length === 0 && typeof errorData?.error === 'string') {
            const raw = errorData.error.trim();
            const detailsMatch = raw.match(/\sDetails:\s*(\{[\s\S]*\})\s*$/);
            if (detailsMatch) {
                try {
                    const parsed = JSON.parse(detailsMatch[1]);
                    if (parsed && typeof parsed.errors === 'object') {
                        for (const [apiKey, value] of Object.entries(parsed.errors)) {
                            const msg = firstMessage(value);
                            if (!msg) continue;
                            const formField = mapToFormField(apiKey);
                            if (formFields.indexOf(formField) !== -1) {
                                fieldErrors[formField] = msg;
                            }
                        }
                    }
                } catch (error) {
                    console.error('[manage-shipment] Error in parseAddressApiError:', error);
                }
            }
        }

        let message;
        if (Object.keys(fieldErrors).length > 0) {
            message = 'Please correct the errors above.';
        } else {
            const raw = errorData?.error;
            if (typeof raw === 'string' && raw.trim()) {
                const idx = raw.indexOf(' Details:');
                message = idx !== -1 ? raw.substring(0, idx).trim() : raw.trim();
            } else {
                message = 'Failed to update address.';
            }
        }
        const err = new Error(message);
        if (Object.keys(fieldErrors).length > 0) {
            err.fieldErrors = fieldErrors;
        }
        return err;
    }

    function parseMoneyAmount(value) {
        if (value === null || typeof value === 'undefined') return null;
        if (typeof value === 'object') {
            if ('amount' in value) return parseMoneyAmount(value.amount);
            if ('value' in value) return parseMoneyAmount(value.value);
            if ('price' in value) return parseMoneyAmount(value.price);
            return null;
        }
        if (typeof value === 'number') {
            if (!Number.isFinite(value)) return null;
            // In Recharge payloads, large integers often represent cents.
            if (Number.isInteger(value) && Math.abs(value) >= 250) return value / 100;
            return value;
        }
        if (typeof value === 'string') {
            const normalized = value.trim().replace(/[$,\s]/g, '');
            if (!normalized) return null;
            const parsed = Number.parseFloat(normalized);
            if (!Number.isFinite(parsed)) return null;
            // If no decimal point and number is large, treat as cents (Recharge convention).
            if (!normalized.includes('.') && Math.abs(parsed) >= 250) return parsed / 100;
            return parsed;
        }
        return null;
    }

    function resolveRechargeUnitPrice(item) {
        if (!item || typeof item !== 'object') return null;
        const firstPrice = Array.isArray(item.prices) ? item.prices[0] : null;
        const firstPlan = Array.isArray(firstPrice?.plans) ? firstPrice.plans[0] : null;
        const firstMinVariant = Array.isArray(item.minimum_variant_prices) ? item.minimum_variant_prices[0] : null;
        const candidates = [
            item.price,
            item.unit_price,
            item.unitPrice,
            item.price_amount,
            item.amount,
            item.variant_price,
            firstPrice?.discounted_price,
            firstPrice?.unit_price,
            firstPlan?.discounted_price,
            firstPlan?.price,
            firstMinVariant?.unit_price,
            firstMinVariant?.price,
            item?.pricing?.price,
            item?.pricing?.amount,
        ];
        for (const candidate of candidates) {
            const parsed = parseMoneyAmount(candidate);
            if (Number.isFinite(parsed) && parsed >= 0) return parsed;
        }
        return null;
    }

    function getApiBaseUrl() {
        return (
            window.EveryTable?.api?.baseUrl ||
            window.etAffinityRescheduleConfig?.apiBaseUrl ||
            'https://admin-app.everytable-sh.com/api'
        );
    }

    function showToast(message, tone) {
        const toast = document.createElement('div');
        toast.className = `et-update-meals-toast et-update-meals-toast--${tone}`;
        toast.textContent = message;
        document.body.appendChild(toast);
        window.setTimeout(() => {
            toast.classList.add('is-visible');
        }, 10);
        window.setTimeout(() => {
            toast.classList.remove('is-visible');
            window.setTimeout(() => toast.remove(), 250);
        }, 2500);
    }

    function showErrorToast(message) {
        showToast(message, 'error');
    }

    function showSuccessToast(message) {
        showToast(message, 'success');
    }

    async function getPortalSession() {
        // Delegate to the shared util like every other portal surface, so this
        // one also gets the Shopify App Proxy fallback for a lapsed portal
        // token (SHOPIFY-3HH). The local cache below stays only as the
        // util-missing degradation. Note this module still has no read-401
        // retry of its own -- separate gap, tracked separately.
        const sharedGetPortalSession = window.etRechargeUtils?.getPortalSession;
        if (typeof sharedGetPortalSession === 'function') {
            return sharedGetPortalSession();
        }
        if (state.sessionPromise) return state.sessionPromise;

        state.sessionPromise = (async () => {
            await ensureSdkInitialized();
            return loginCustomerPortal();
        })();

        try {
            return await state.sessionPromise;
        } catch (error) {
            state.sessionPromise = null;
            throw error;
        }
    }

    async function ensureSdkInitialized() {
        if (state.sdkInitialized) return;
        const sharedSdk = getSharedRechargeSdk();
        if (typeof sharedSdk.ready !== 'function') {
            throw new Error(
                'Shared Recharge SDK is unavailable. Ensure recharge-sdk-bootstrap.js initializes before affinity-manage-shipment.js.'
            );
        }
        await sharedSdk.ready();
        state.sdkInitialized = true;
    }

    function normalizeSubscriptionList(response) {
        if (!response || typeof response !== 'object') return [];
        if (Array.isArray(response.subscriptions)) return response.subscriptions;
        if (Array.isArray(response.data)) return response.data;
        if (Array.isArray(response.results)) return response.results;
        return [];
    }

    async function getSubscriptionById(session, subscriptionId) {
        const response = await getSubscription(session, subscriptionId);

        if (response?.subscription && typeof response.subscription === 'object') {
            return response.subscription;
        }
        if (response && typeof response === 'object') {
            return response;
        }
        throw new Error(`Subscription ${subscriptionId} was not returned by API.`);
    }

    async function resolveBundleSelectionId(session, subscription) {
        const candidates = [
            subscription?.bundle_selection_id,
            subscription?.bundleSelectionId,
            subscription?.bundle_selection?.id,
            subscription?.bundleSelection?.id,
        ];
        for (const candidate of candidates) {
            const parsed = parseOptionalSubscriptionId(candidate);
            if (parsed) return parsed;
        }
        try {
            const response = await listBundleSelections(session, { limit: 250 });
            const selections = Array.isArray(response?.bundle_selections)
                ? response.bundle_selections
                : Array.isArray(response?.bundleSelections)
                    ? response.bundleSelections
                    : Array.isArray(response?.data)
                        ? response.data
                        : Array.isArray(response?.results)
                            ? response.results
                            : [];
            if (!selections.length) return null;
            const subscriptionId = parseOptionalSubscriptionId(subscription?.id);
            const purchaseItemId = parseOptionalSubscriptionId(subscription?.purchase_item_id);
            const match = selections.find((entry) => {
                const entryPurchaseItemId = parseOptionalSubscriptionId(entry?.purchase_item_id);
                if (!entryPurchaseItemId) return false;
                return (
                    (subscriptionId && entryPurchaseItemId === subscriptionId) ||
                    (purchaseItemId && entryPurchaseItemId === purchaseItemId)
                );
            });
            if (match) return parseOptionalSubscriptionId(match.id);
            return null;
        } catch (_) {
            return null;
        }
    }

    async function getBundleSelectionById(session, bundleSelectionId) {
        const response = await getBundleSelection(session, bundleSelectionId);
        if (response?.bundle_selection && typeof response.bundle_selection === 'object') {
            return response.bundle_selection;
        }
        if (response && typeof response === 'object') {
            return response;
        }
        throw new Error(`Bundle selection ${bundleSelectionId} was not returned by API.`);
    }

    function extractItemsFromBundleSelection(bundleSelection) {
        if (!bundleSelection || typeof bundleSelection !== 'object') return [];
        if (Array.isArray(bundleSelection.items)) return bundleSelection.items;
        if (Array.isArray(bundleSelection.bundle_selections)) return bundleSelection.bundle_selections;
        if (Array.isArray(bundleSelection.bundleSelections)) return bundleSelection.bundleSelections;
        return [];
    }

    function resolveSubscriptionAddressId(subscription) {
        const candidates = [
            subscription?.address_id,
            subscription?.addressId,
            subscription?.address?.id,
            subscription?.address?.address_id,
            subscription?.shipping_address_id,
            subscription?.shippingAddressId,
        ];
        for (const candidate of candidates) {
            const parsed = parseOptionalSubscriptionId(candidate);
            if (parsed) return parsed;
        }
        return null;
    }

    function normalizeAddressList(response) {
        if (!response || typeof response !== 'object') return [];
        if (Array.isArray(response.addresses)) return response.addresses;
        if (Array.isArray(response.data)) return response.data;
        if (Array.isArray(response.results)) return response.results;
        return [];
    }

    async function resolveSubscriptionAddress(session, subscription) {
        const addressId = resolveSubscriptionAddressId(subscription);
        if (!addressId || !session) return null;

        try {
            const response = await getAddress(session, addressId);
            if (response?.address && typeof response.address === 'object') {
                return response.address;
            }
            if (
                response &&
                typeof response === 'object' &&
                parseOptionalSubscriptionId(response.id) === addressId
            ) {
                return response;
            }
        } catch (error) {
            // Some SDK footprints do not expose address.getAddress; fallback to list.
            console.error('[manage-shipment] Error in resolveSubscriptionAddress:', error);
        }

        try {
            const response = await listAddresses(session, { limit: 250 });
            const addresses = normalizeAddressList(response);
            const match = addresses.find(
                (entry) =>
                    parseOptionalSubscriptionId(entry?.id) === addressId ||
                    parseOptionalSubscriptionId(entry?.address_id) === addressId
            );
            if (match && typeof match === 'object') return match;
        } catch (error) {
            // If address APIs are unavailable, we gracefully continue.
            console.error('[manage-shipment] Error in resolveSubscriptionAddress:', error);
        }

        return null;
    }

    function pickPrimaryOverviewSubscription(subscriptions) {
        const safeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];
        if (!safeSubscriptions.length) return null;

        const activeSubscriptions = safeSubscriptions.filter((entry) => {
            const status = String(entry?.status || '')
                .trim()
                .toLowerCase();
            return status === 'active';
        });
        const prioritized = activeSubscriptions.length
            ? activeSubscriptions
            : safeSubscriptions;

        const withAddress = prioritized.filter((entry) =>
            Boolean(resolveSubscriptionAddressId(entry))
        );
        const sortable = (withAddress.length ? withAddress : prioritized).slice();

        sortable.sort((left, right) => {
            const leftTimestamp = parseSubscriptionNextChargeTimestamp(left);
            const rightTimestamp = parseSubscriptionNextChargeTimestamp(right);
            return leftTimestamp - rightTimestamp;
        });

        return sortable[0] || null;
    }

    function parseSubscriptionNextChargeTimestamp(subscription) {
        const candidates = [
            subscription?.next_charge_date,
            subscription?.next_charge_scheduled_at,
            subscription?.next_charge_at,
            subscription?.nextChargeDate,
            subscription?.nextChargeScheduledAt,
            subscription?.nextChargeAt,
        ];

        for (const candidate of candidates) {
            const parsed = parseSubscriptionDateValue(candidate);
            if (parsed) return parsed.getTime();
        }

        return Number.MAX_SAFE_INTEGER;
    }

    function parseSubscriptionDateValue(rawValue) {
        const raw = String(rawValue || '').trim();
        if (!raw) return null;

        const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            const year = parseInteger(match[1], Number.NaN);
            const monthIndex = parseInteger(match[2], Number.NaN) - 1;
            const day = parseInteger(match[3], Number.NaN);
            if (
                Number.isFinite(year) &&
                Number.isFinite(monthIndex) &&
                Number.isFinite(day)
            ) {
                const parsed = new Date(year, monthIndex, day);
                if (
                    parsed.getFullYear() === year &&
                    parsed.getMonth() === monthIndex &&
                    parsed.getDate() === day
                ) {
                    return parsed;
                }
            }
        }

        const timestamp = Date.parse(raw);
        if (!Number.isFinite(timestamp)) return null;
        const parsed = new Date(timestamp);
        if (!Number.isFinite(parsed.getTime())) return null;
        return parsed;
    }

    async function getSubscriptions(session) {
        const response = await listSubscriptions(session, { limit: 250 });
        return normalizeSubscriptionList(response);
    }

    function extractPickupLocationIdFromAddress(address) {
        if (!address || typeof address !== 'object') return null;

        const orderAttributes = address.order_attributes || address.orderAttributes || [];
        for (const attr of orderAttributes) {
            if (!attr || typeof attr !== 'object') continue;

            let key = null;
            let value = null;

            if ('name' in attr && 'value' in attr) {
                key = String(attr.name || '').trim();
                value = String(attr.value || '').trim();
            } else {
                const keys = Object.keys(attr);
                if (keys.length > 0) {
                    key = keys[0];
                    value = String(attr[key] || '').trim();
                }
            }

            if (key && normalizeCustomAttributeKey(key) === 'locationid') {
                return value || null;
            }
        }

        return null;
    }

    function buildDeliveryAddressModalMarkup(address, subscriptionId, subscription = null) {
        const address1 = String(address?.address1 || '').trim();
        const address2 = String(address?.address2 || '').trim();
        const city = String(address?.city || '').trim();
        const province = String(address?.province || address?.state || '').trim();
        const zip = String(address?.zip || address?.zip_code || '').trim();

        const initialTime = extractExistingDeliveryTime(subscription, address);
        let deliveryInstructions = '';
        const orderAttributes = address?.order_attributes || address?.orderAttributes || [];
        if (Array.isArray(orderAttributes)) {
            for (const attr of orderAttributes) {
                if (!attr || typeof attr !== 'object') continue;
                let key = null;
                let value = null;

                if ('name' in attr && 'value' in attr) {
                    key = String(attr.name || '').trim();
                    value = String(attr.value || '').trim();
                } else {
                    const keys = Object.keys(attr);
                    if (keys.length > 0) {
                        key = keys[0];
                        value = String(attr[key] || '').trim();
                    }
                }

                if (key && normalizeCustomAttributeKey(key) === 'deliveryinstructions') {
                    deliveryInstructions = value;
                    break;
                }
            }
        }

        return `
      <div class="et-shipment-modal-overlay recharge-theme" data-et-shipment-modal="true">
        <div class="et-shipment-modal-container">
          <div class="et-shipment-modal-header">
            <h2 class="et-shipment-modal-title">Manage Delivery</h2>
            <button type="button" class="et-shipment-modal-close" data-action="et-close-shipment-modal" aria-label="Close">×</button>
          </div>
          <div class="et-shipment-modal-body">
            <form class="et-shipment-modal-form">
              <div class="et-shipment-modal-field" data-field="address1">
                <label for="delivery-address1">Address Line 1 *</label>
                <input type="text" id="delivery-address1" name="address1" value="${escapeHtml(address1)}" autocomplete="address-line1">
                <span class="et-shipment-modal-field-error" data-field-error="address1" aria-live="polite"></span>
              </div>
              <div class="et-shipment-modal-field" data-field="address2">
                <label for="delivery-address2">Address Line 2</label>
                <input type="text" id="delivery-address2" name="address2" value="${escapeHtml(address2)}" autocomplete="address-line2">
                <span class="et-shipment-modal-field-error" data-field-error="address2" aria-live="polite"></span>
              </div>
              <div class="et-shipment-modal-field-row">
                <div class="et-shipment-modal-field" data-field="city">
                  <label for="delivery-city">City *</label>
                  <input type="text" id="delivery-city" name="city" value="${escapeHtml(city)}" autocomplete="address-level2">
                  <span class="et-shipment-modal-field-error" data-field-error="city" aria-live="polite"></span>
                </div>
                <div class="et-shipment-modal-field" data-field="state">
                  <label for="delivery-state">State *</label>
                  <input type="text" id="delivery-state" name="state" value="${escapeHtml(province)}" autocomplete="address-level1">
                  <span class="et-shipment-modal-field-error" data-field-error="state" aria-live="polite"></span>
                </div>
              </div>
              <div class="et-shipment-modal-field-row et-shipment-modal-field-row--zip-time">
                <div class="et-shipment-modal-field" data-field="zip">
                  <label for="delivery-zip">ZIP Code *</label>
                  <input type="text" id="delivery-zip" name="zip" value="${escapeHtml(zip)}" inputmode="numeric" pattern="[0-9]{5}" maxlength="5" autocomplete="postal-code">
                  <span class="et-shipment-modal-field-error" data-field-error="zip" aria-live="polite"></span>
                </div>
                <div class="et-shipment-modal-field" data-field="deliveryTime">
                  <label for="delivery-time">Preferred Delivery Time *</label>
                  <select id="delivery-time" name="deliveryTime" data-initial-time="${escapeHtml(initialTime)}">
                    <option value="">Select a time</option>
                  </select>
                  <span class="et-shipment-modal-field-error" data-field-error="deliveryTime" aria-live="polite"></span>
                </div>
              </div>
              <div class="et-shipment-modal-field" data-field="deliveryInstructions">
                <label for="delivery-instructions">Delivery Instructions</label>
                <textarea id="delivery-instructions" name="deliveryInstructions" rows="3" maxlength="280" placeholder="Enter delivery instructions (e.g., gate code, building access, special instructions)">${escapeHtml(deliveryInstructions)}</textarea>
                <div style="font-size: 12px; color: #666; margin-top: 4px; text-align: right;">
                    <span id="delivery-instructions-count">${deliveryInstructions.length}</span>/280 characters
                </div>
                <span class="et-shipment-modal-field-error" data-field-error="deliveryInstructions" aria-live="polite"></span>
              </div>
              <div class="et-shipment-modal-error" data-role="delivery-error" style="display: none;"></div>
            </form>
          </div>
          <div class="et-shipment-modal-footer">
            <button type="button" class="et-shipment-modal-switch-link" data-action="et-switch-to-pickup" data-subscription-id="${String(subscriptionId)}">
              Switch to Pickup
            </button>
            <div class="et-shipment-modal-footer-actions">
              <button type="button" class="et-shipment-modal-button et-shipment-modal-button--secondary" data-action="et-close-shipment-modal">Cancel</button>
              <button type="button" class="et-shipment-modal-button et-shipment-modal-button--primary" data-action="et-save-delivery-address" data-subscription-id="${String(subscriptionId)}">Save</button>
            </div>
          </div>
        </div>
      </div>
    `;
    }

    function buildPickupLocationModalMarkup(subscriptionId, currentLocationId) {
        return `
      <div class="et-shipment-modal-overlay recharge-theme" data-et-shipment-modal="true">
        <div class="et-shipment-modal-container">
          <div class="et-shipment-modal-header">
            <h2 class="et-shipment-modal-title">Manage Pickup</h2>
            <button type="button" class="et-shipment-modal-close" data-action="et-close-shipment-modal" aria-label="Close">×</button>
          </div>
          <div class="et-shipment-modal-body">
            <div class="et-shipment-modal-loading" data-role="pickup-loading">Loading pickup locations...</div>
            <div class="et-shipment-modal-error" data-role="pickup-error" style="display: none;"></div>
            <div class="et-shipment-modal-locations" data-role="pickup-locations" style="display: none;">
              <div class="et-shipment-modal-form">
                <div class="et-shipment-modal-locations-list" data-role="locations-list"></div>
              </div>
            </div>
          </div>
          <div class="et-shipment-modal-footer">
            <button type="button" class="et-shipment-modal-switch-link" data-action="et-switch-to-delivery" data-subscription-id="${String(subscriptionId)}">
              Switch to Delivery
            </button>
            <div class="et-shipment-modal-footer-actions">
              <button type="button" class="et-shipment-modal-button et-shipment-modal-button--secondary" data-action="et-close-shipment-modal">Cancel</button>
              <button type="button" class="et-shipment-modal-button et-shipment-modal-button--primary" data-action="et-save-pickup-location" data-subscription-id="${String(subscriptionId)}" disabled>Save</button>
            </div>
          </div>
        </div>
      </div>
    `;
    }

    function startDeliveryOpenSpan() {
        endDeliveryOpenSpan({ 'et.portal.outcome': 'superseded' });
        const utils = window.etRechargeUtils;
        // `availability_ms` sums the admin-app availability calls; they run one
        // after another, so the sum is the time spent waiting on them.
        state.deliveryOpenSpan = utils?.startPortalSpan?.('manage-delivery-open', {
            spanName: 'subscription.manage_delivery.open',
            attributes: {
                'et.surface': 'affinity-manage-shipment',
                'et.component': 'delivery-modal',
            },
            counts: { 'et.portal.availability': utils.PORTAL_RESOURCE_PATTERNS?.availability },
        }) || null;
    }

    // Ends the Manage Delivery span, if one is running. Pass `onlySpan` to end
    // it only while that span is still the current one, so a stale modal's
    // late callback cannot close a newer open's span.
    function endDeliveryOpenSpan(attributes, onlySpan) {
        const span = state.deliveryOpenSpan;
        if (typeof onlySpan !== 'undefined' && span !== onlySpan) return;
        state.deliveryOpenSpan = null;
        span?.end(attributes);
    }

    function createModalContainer(markup) {
        const container = document.createElement('div');
        container.innerHTML = markup;
        const overlay = container.querySelector('[data-et-shipment-modal="true"]');
        if (overlay) {
            overlay._modalContainer = container;
        }
        return container;
    }

    function bindDeliveryModalHandlers(container, subscriptionId, currentAddress, subscription = null) {
        const overlay = container.querySelector('[data-et-shipment-modal="true"]');
        if (!overlay) return;

        // The span this modal's open started; its callbacks end only that one.
        const modalOpenSpan = state.deliveryOpenSpan;

        const closeHandler = () => {
            endDeliveryOpenSpan({ 'et.portal.outcome': 'closed' }, modalOpenSpan);
            overlay.remove();
            state.modalOpen = false;
            state.modalType = null;
        };

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeHandler();
            }
        });

        const closeButtons = overlay.querySelectorAll('[data-action="et-close-shipment-modal"]');
        closeButtons.forEach((btn) => {
            btn.addEventListener('click', closeHandler);
        });

        const instructionsTextarea = overlay.querySelector('#delivery-instructions');
        const instructionsCount = overlay.querySelector('#delivery-instructions-count');

        if (instructionsTextarea && instructionsCount) {
            instructionsTextarea.addEventListener('input', () => {
                instructionsCount.textContent = instructionsTextarea.value.length;
            });
        }

        function setDeliveryFieldError(fieldName, message) {
            const fieldWrap = overlay.querySelector(`.et-shipment-modal-field[data-field="${fieldName}"]`);
            const errorSpan = overlay.querySelector(`[data-field-error="${fieldName}"]`);
            if (fieldWrap) {
                if (message) {
                    fieldWrap.classList.add('et-shipment-modal-field--error');
                } else {
                    fieldWrap.classList.remove('et-shipment-modal-field--error');
                }
            }
            if (errorSpan) {
                errorSpan.textContent = message || '';
            }
        }

        async function loadDeliveryTimeOptions(zip, selectedTime = '') {
            const timeSelect = overlay.querySelector('#delivery-time');
            if (!timeSelect) return;

            timeSelect.disabled = true;
            timeSelect.innerHTML = '<option value="">Loading times...</option>';
            // Whatever replaces "Loading times..." ends the open span; only the
            // first load has one, so later ZIP changes record nothing.
            let timesOutcome = 'error';

            try {
                const apiBaseUrl = getApiBaseUrl();
                const availabilityResp = await fetch(`${apiBaseUrl}/search/availability/${encodeURIComponent(zip)}`);

                if (!availabilityResp.ok) {
                    throw new Error('Unable to load delivery times');
                }

                const availabilityData = await availabilityResp.json();
                const deliveryLocation = availabilityData?.deliveryLocation;

                if (!deliveryLocation?.location_id) {
                    timeSelect.innerHTML = '<option value="">No delivery times available</option>';
                    timeSelect.disabled = false;
                    timesOutcome = 'no-delivery-location';
                    return;
                }

                // Get next delivery date based on subscription
                let nextDeliveryDate = null;
                const chargeDate = subscription?.next_charge_scheduled_at ? new Date(subscription.next_charge_scheduled_at) : null;
                if (chargeDate && Number.isFinite(chargeDate.getTime())) {
                    nextDeliveryDate = new Date(chargeDate);
                    nextDeliveryDate.setDate(nextDeliveryDate.getDate() + 2);
                } else {
                    nextDeliveryDate = new Date();
                    nextDeliveryDate.setDate(nextDeliveryDate.getDate() + 2);
                }
                const targetDateStr = formatDateInputValue(nextDeliveryDate);

                // Fetch detailed location info to get availability for the specific date
                const locationResp = await fetch(`${apiBaseUrl}/search/availability/location/${encodeURIComponent(deliveryLocation.location_id)}`);
                let openingTime = null;
                let closingTime = null;

                if (locationResp.ok) {
                    const locationData = await locationResp.json();
                    const pickupLocations = Array.isArray(locationData?.pickupLocations) ? locationData.pickupLocations : [];
                    const matchingLocation = pickupLocations.find(loc => String(loc.location_id || loc.id) === String(deliveryLocation.location_id));
                    
                    if (matchingLocation && Array.isArray(matchingLocation.pickupDates)) {
                        const dateEntry = matchingLocation.pickupDates.find(d => d.date === targetDateStr && !d.isClosed);
                        if (dateEntry) {
                            openingTime = dateEntry.open || openingTime;
                            closingTime = dateEntry.close || closingTime;
                        }
                    }
                }

                const timeSlots = generateDeliveryTimeSlots(openingTime, closingTime);

                timesOutcome = timeSlots.length === 0 ? 'no-times' : 'times-loaded';
                if (timeSlots.length === 0) {
                    timeSelect.innerHTML = '<option value="">No delivery times available</option>';
                } else {
                    const normalizedSelected = normalizeQuarterHourTime(selectedTime);
                    const slotExists = timeSlots.some(s => s.value === normalizedSelected);
                    timeSelect.innerHTML = formatTimeSlotOptions(timeSlots, slotExists ? normalizedSelected : '');
                }

                timeSelect.disabled = false;
            } catch (error) {
                console.warn('[manage-shipment] Error loading delivery times:', error);
                if(timeSelect) {
                    timeSelect.innerHTML = '<option value="">Unable to load delivery times</option>';
                    timeSelect.disabled = false;
                }
                showErrorToast('Unable to load delivery times. Please try again.');
            } finally {
                if (modalOpenSpan) {
                    endDeliveryOpenSpan({ 'et.portal.outcome': timesOutcome }, modalOpenSpan);
                }
            }
        }

        function isDeliveryFormValid() {
            const form = overlay.querySelector('form');
            if (!form) return false;
            const get = (name) => {
                const el = form.querySelector(`[name="${name}"]`);
                return el ? String(el.value || '').trim() : '';
            };
            const fields = [
                { name: 'address1', value: get('address1') },
                { name: 'address2', value: get('address2') },
                { name: 'city', value: get('city') },
                { name: 'state', value: get('state') },
                { name: 'zip', value: get('zip') },
                { name: 'deliveryTime', value: get('deliveryTime') },
                { name: 'deliveryInstructions', value: get('deliveryInstructions') },
            ];
            return fields.every((f) => validateDeliveryField(f.name, f.value).valid);
        }

        function updateDeliverySaveButtonState() {
            const btn = overlay.querySelector('[data-action="et-save-delivery-address"]');
            if (!btn) return;
            if (btn.textContent === 'Saving...') return;
            btn.disabled = !isDeliveryFormValid();
        }

        const deliveryFields = [
            { name: 'address1', id: 'delivery-address1' },
            { name: 'address2', id: 'delivery-address2' },
            { name: 'city', id: 'delivery-city' },
            { name: 'state', id: 'delivery-state' },
            { name: 'zip', id: 'delivery-zip' },
            { name: 'deliveryTime', id: 'delivery-time' },
            { name: 'deliveryInstructions', id: 'delivery-instructions' },
        ];

        let currentZip = '';
        const zipInput = overlay.querySelector('#delivery-zip');
        if (zipInput) {
            currentZip = String(zipInput.value || '').trim();
        }

        deliveryFields.forEach(({ name, id }) => {
            const el = overlay.querySelector(`#${id}`);
            if (!el) return;
            el.addEventListener('blur', () => {
                const value = el.value ? String(el.value).trim() : '';
                const result = validateDeliveryField(name, value);
                setDeliveryFieldError(name, result.valid ? '' : result.message);
                updateDeliverySaveButtonState();
            });
            el.addEventListener('input', () => {
                setDeliveryFieldError(name, '');
                updateDeliverySaveButtonState();
            });
            el.addEventListener('focus', () => setDeliveryFieldError(name, ''));
        });

        // Add ZIP code change handler to reload time options
        if (zipInput) {
            zipInput.addEventListener('change', async () => {
                const newZip = String(zipInput.value || '').trim();
                if (newZip && newZip !== currentZip && newZip.length === 5) {
                    currentZip = newZip;
                    const timeSelect = overlay.querySelector('#delivery-time');
                    const currentTime = timeSelect ? timeSelect.value : '';
                    await loadDeliveryTimeOptions(newZip, currentTime);
                }
            });
        }

        // Initial load of time options if ZIP is present
        if (currentZip && currentZip.length === 5) {
            const timeSelect = overlay.querySelector('#delivery-time');
            const initialTime = timeSelect ? timeSelect.dataset.initialTime : '';
            loadDeliveryTimeOptions(currentZip, initialTime);
        } else {
            // No ZIP means no times to wait for: the modal itself is the end.
            endDeliveryOpenSpan({ 'et.portal.outcome': 'no-zip' }, modalOpenSpan);
        }

        const form = overlay.querySelector('form');
        if (form) {
            form.addEventListener('input', updateDeliverySaveButtonState);
            form.addEventListener('change', updateDeliverySaveButtonState);
            updateDeliverySaveButtonState();
        }

        const switchToPickupBtn = overlay.querySelector('[data-action="et-switch-to-pickup"]');
        if (switchToPickupBtn) {
            switchToPickupBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                closeHandler();
                window.etManageShipment.openPickup(subscriptionId);
            });
        }

        const saveBtn = overlay.querySelector('.et-shipment-modal-footer [data-action="et-save-delivery-address"]');
        if (!saveBtn) {
            console.error('[manage-shipment] Delivery save button not found in modal footer');
            return;
        }

        saveBtn.addEventListener('click', async function handleDeliverySaveClick(e) {
            if (e.target !== this && !this.contains(e.target)) {
                return;
            }

            if (!this.hasAttribute('data-action') || this.getAttribute('data-action') !== 'et-save-delivery-address') {
                return;
            }

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Disable button during save
            const button = this;
            const originalText = button.textContent;
            const originalDisabled = button.disabled;

            if (!button.disabled) {
                button.disabled = true;
                button.textContent = 'Saving...';
            }

            try {
                const currentOverlay = document.querySelector('[data-et-shipment-modal="true"]');
                if (!currentOverlay) {
                    throw new Error('Modal not found. Please refresh and try again.');
                }
                await handleSaveDeliveryAddress(currentOverlay, subscriptionId, currentAddress);
            } catch (error) {
                console.error('[manage-shipment] Error in delivery save button handler:', error);
                // Re-enable button on error
                button.disabled = originalDisabled;
                button.textContent = originalText;

                const currentOverlay = document.querySelector('[data-et-shipment-modal="true"]');
                const errorEl = currentOverlay?.querySelector('[data-role="delivery-error"]');
                const fieldErrors = error?.fieldErrors && typeof error.fieldErrors === 'object' ? error.fieldErrors : null;
                if (fieldErrors && Object.keys(fieldErrors).length > 0 && currentOverlay) {
                    const deliveryFieldNames = ['address1', 'address2', 'city', 'state', 'zip', 'deliveryInstructions'];
                    deliveryFieldNames.forEach((name) => {
                        const msg = fieldErrors[name] || '';
                        const fieldWrap = currentOverlay.querySelector(`.et-shipment-modal-field[data-field="${name}"]`);
                        const errorSpan = currentOverlay.querySelector(`[data-field-error="${name}"]`);
                        if (fieldWrap) fieldWrap.classList.toggle('et-shipment-modal-field--error', !!msg);
                        if (errorSpan) errorSpan.textContent = msg;
                    });
                    if (errorEl) {
                        errorEl.textContent = error?.message || 'Please correct the errors above.';
                        errorEl.style.display = 'block';
                    }
                } else {
                    if (errorEl) {
                        errorEl.textContent = error?.message || 'Unable to save delivery address. Please try again.';
                        errorEl.style.display = 'block';
                    }
                    showErrorToast(error?.message || 'Unable to save delivery address. Please try again.');
                }
            }
        });

        // Prevent form submission on Enter key - only allow on Save button click
        if (form) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                e.stopPropagation();
            });

            const inputs = form.querySelectorAll('input[type="text"], textarea');
            inputs.forEach((input) => {
                input.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' && input.tagName !== 'TEXTAREA') {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                });
            });
        }
    }

    async function bindPickupModalHandlers(container, subscriptionId, address, currentLocationId) {
        const overlay = container.querySelector('[data-et-shipment-modal="true"]');
        if (!overlay) return;

        const closeHandler = () => {
            overlay.remove();
            state.modalOpen = false;
            state.modalType = null;
        };

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeHandler();
            }
        });

        const closeButtons = overlay.querySelectorAll('[data-action="et-close-shipment-modal"]');
        closeButtons.forEach((btn) => {
            btn.addEventListener('click', closeHandler);
        });

        const switchToDeliveryBtn = overlay.querySelector('[data-action="et-switch-to-delivery"]');
        if (switchToDeliveryBtn) {
            switchToDeliveryBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                closeHandler();
                window.etManageShipment.openDelivery(subscriptionId);
            });
        }

        await loadPickupLocations(overlay, currentLocationId, address);

        const saveBtn = overlay.querySelector('[data-action="et-save-pickup-location"]');
        if (!saveBtn) {
            console.error('[manage-shipment] Save button not found in pickup modal');
            return;
        }

        saveBtn.addEventListener('click', async function handleSaveClick(e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Disable button during save
            const button = this;
            const originalText = button.textContent;
            const originalDisabled = button.disabled;

            button.disabled = true;
            button.textContent = 'Saving...';

            try {
                await handleSavePickupLocation(subscriptionId, address);
            } catch (error) {
                console.error('[manage-shipment] Error in save button handler:', error);

                button.disabled = originalDisabled;
                button.textContent = originalText;

                showErrorToast(error?.message || 'Unable to save pickup location. Please try again.');
            }
        }, true);
    }

    async function loadPickupLocations(overlay, selectedLocationId, customerAddress) {
        const loadingEl = overlay.querySelector('[data-role="pickup-loading"]');
        const errorEl = overlay.querySelector('[data-role="pickup-error"]');
        const locationsEl = overlay.querySelector('[data-role="pickup-locations"]');
        const locationsListEl = overlay.querySelector('[data-role="locations-list"]');

        let apiLocationId = selectedLocationId || null;

        if (!apiLocationId) {
            const customerZip = customerAddress?.zip || customerAddress?.zip_code;
            if (customerZip) {
                try {
                    const apiBaseUrl = getApiBaseUrl();
                    const availabilityResp = await fetch(`${apiBaseUrl}/search/availability/${encodeURIComponent(customerZip)}`);
                    if (availabilityResp.ok) {
                        const availabilityData = await availabilityResp.json();
                        if (availabilityData?.deliveryLocation?.location_id) {
                            apiLocationId = String(availabilityData.deliveryLocation.location_id);
                        }
                    }
                } catch (error) {
                    console.warn('[manage-shipment] Unable to fetch delivery location for zip:', error);
                }
            }
        }

        if (!apiLocationId) {
            try {
                const session = await getPortalSession();
                const subscriptions = await getSubscriptions(session);
                const primarySubscription = pickPrimaryOverviewSubscription(subscriptions);
                if (primarySubscription) {
                    const address = await resolveSubscriptionAddress(session, primarySubscription);
                    if (address?.zip) {
                        const apiBaseUrl = getApiBaseUrl();
                        const availabilityResp = await fetch(`${apiBaseUrl}/search/availability/${encodeURIComponent(address.zip)}`);
                        if (availabilityResp.ok) {
                            const availabilityData = await availabilityResp.json();
                            if (availabilityData?.deliveryLocation?.location_id) {
                                apiLocationId = String(availabilityData.deliveryLocation.location_id);
                            }
                        }
                    }
                }
            } catch (error) {
                console.warn('[manage-shipment] Unable to fetch delivery location from primary subscription:', error);
            }
        }

        if (!apiLocationId) {
            if (loadingEl) loadingEl.style.display = 'none';
            if (errorEl) {
                errorEl.textContent = 'Unable to determine current location. Please try again.';
                errorEl.style.display = 'block';
            }
            return;
        }

        try {
            const apiBaseUrl = getApiBaseUrl();
            const response = await fetch(`${apiBaseUrl}/search/availability/location/${encodeURIComponent(apiLocationId)}`);

            if (!response.ok) {
                throw new Error('Failed to load pickup locations');
            }

            const data = await response.json();
            const pickupLocations = Array.isArray(data?.pickupLocations) ? data.pickupLocations : [];

            if (pickupLocations.length === 0) {
                if (loadingEl) loadingEl.style.display = 'none';
                if (errorEl) {
                    errorEl.textContent = 'No pickup locations available.';
                    errorEl.style.display = 'block';
                }
                return;
            }

            // Sort by distance_from_entered_zip_code (closest to farthest), then by name
            const sortedLocations = pickupLocations.slice().sort((a, b) => {
                const aDistance = Number.parseFloat(a?.distance_from_entered_zip_code || a?.distance || Number.MAX_SAFE_INTEGER);
                const bDistance = Number.parseFloat(b?.distance_from_entered_zip_code || b?.distance || Number.MAX_SAFE_INTEGER);
                if (aDistance !== bDistance) {
                    return aDistance - bDistance;
                }
                const aName = String(a?.name || '').trim();
                const bName = String(b?.name || '').trim();
                return aName.localeCompare(bName);
            });

            if (locationsListEl) {
                const normalizedSelectedLocationId = selectedLocationId ? String(selectedLocationId).trim() : null;
                const hasSelectedLocationInList = normalizedSelectedLocationId
                    ? sortedLocations.some((loc) => {
                        const locId = String(
                            loc?.location_id || loc?.locationId || loc?.shopifyLocation?.id?.split('/').pop() || ''
                        ).trim();
                        return locId === normalizedSelectedLocationId;
                    })
                    : false;

                const locationsHTML = sortedLocations.map((location, index) => {
                    const locationId = String(
                        location?.location_id ||
                        location?.locationId ||
                        location?.shopifyLocation?.id?.split('/').pop() ||
                        ''
                    ).trim();
                    const locationName = String(location?.name || location?.shopifyLocation?.name || 'Unknown Location').trim();
                    const address = location?.address || location?.shopifyLocation?.address || {};
                    const addressLine = [
                        address.address1,
                        address.city,
                        address.provinceCode || address.province,
                        address.zip
                    ].filter(Boolean).join(', ');
                    const isSelected = hasSelectedLocationInList
                        ? locationId === normalizedSelectedLocationId
                        : index === 0;

                    const distance = location?.distance_from_entered_zip_code || location?.distance;
                    const distanceText = distance !== undefined && distance !== null && !Number.isNaN(Number.parseFloat(distance))
                        ? `${Number.parseFloat(distance).toFixed(1)} mi`
                        : '';

                    return `
            <label class="et-shipment-modal-location-option ${isSelected ? 'et-shipment-modal-location-option--selected' : ''}">
              <input type="radio" name="pickup-location" value="${escapeHtml(locationId)}" ${isSelected ? 'checked' : ''} data-location-name="${escapeHtml(locationName)}">
              <div class="et-shipment-modal-location-info">
                <div class="et-shipment-modal-location-name">${escapeHtml(locationName)}</div>
                ${addressLine ? `<div class="et-shipment-modal-location-address">${escapeHtml(addressLine)}</div>` : ''}
              </div>
              ${distanceText ? `<div class="et-shipment-modal-location-distance">${escapeHtml(distanceText)}</div>` : ''}
            </label>
          `;
                }).join('');

                locationsListEl.innerHTML = locationsHTML;

                const radioButtons = locationsListEl.querySelectorAll('input[type="radio"]');

                if (radioButtons.length === 0) {
                    console.error('[manage-shipment] No radio buttons created! HTML:', locationsHTML.substring(0, 500));
                }
                radioButtons.forEach((radio) => {
                    radio.addEventListener('change', (e) => {
                        e.stopPropagation();
                        e.stopImmediatePropagation();

                        const locationId = String(radio.value || '').trim();
                        const locationName = String(radio.getAttribute('data-location-name') || '').trim();

                        if (radio.checked && locationId) {
                            overlay.dataset.selectedLocationId = locationId;
                            overlay.dataset.selectedLocationName = locationName;
                        }

                        const currentSaveBtn = overlay.querySelector('[data-action="et-save-pickup-location"]');
                        if (currentSaveBtn) {
                            currentSaveBtn.disabled = false;
                        }

                        locationsListEl.querySelectorAll('.et-shipment-modal-location-option').forEach((option) => {
                            option.classList.remove('et-shipment-modal-location-option--selected');
                        });
                        radio.closest('.et-shipment-modal-location-option')?.classList.add('et-shipment-modal-location-option--selected');
                    });

                    radio.addEventListener('click', (e) => {
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                    });
                });
            }

            if (loadingEl) loadingEl.style.display = 'none';
            if (locationsEl) locationsEl.style.display = 'block';

            const currentSaveBtn = overlay.querySelector('[data-action="et-save-pickup-location"]');
            if (currentSaveBtn && sortedLocations.length > 0) {
                const checkedRadio = overlay.querySelector('input[name="pickup-location"]:checked');
                if (checkedRadio) {
                    const locationId = String(checkedRadio.value || '').trim();
                    const locationName = String(checkedRadio.getAttribute('data-location-name') || '').trim();
                    if (locationId) {
                        overlay.dataset.selectedLocationId = locationId;
                        overlay.dataset.selectedLocationName = locationName;
                    }
                    currentSaveBtn.disabled = false;
                }
            }
        } catch (error) {
            if (loadingEl) loadingEl.style.display = 'none';
            if (errorEl) {
                errorEl.textContent = 'Unable to load pickup locations. Please try again.';
                errorEl.style.display = 'block';
            }
        }
    }

    function setDeliveryModalSaveInProgress(overlayEl, inProgress) {
        if (!overlayEl) return;
        const form = overlayEl.querySelector('form');
        if (form) {
            form.querySelectorAll('input, textarea, select').forEach((el) => {
                el.disabled = inProgress;
            });
        }
        overlayEl.querySelectorAll(
            '[data-action="et-close-shipment-modal"], [data-action="et-switch-to-pickup"], [data-action="et-cancel-subscription"]'
        ).forEach((el) => {
            if (el) el.disabled = inProgress;
        });
        const saveBtn = overlayEl.querySelector('[data-action="et-save-delivery-address"]');
        if (saveBtn) saveBtn.disabled = inProgress;
    }

    async function handleSaveDeliveryAddress(overlay, subscriptionId, currentAddress) {
        const currentOverlay = document.querySelector('[data-et-shipment-modal="true"]');
        if (!currentOverlay) {
            console.error('[manage-shipment] Delivery modal overlay not found in document');
            throw new Error('Modal not found. Please refresh and try again.');
        }
        overlay = currentOverlay;

        const form = overlay.querySelector('form');
        if (!form) {
            console.error('[manage-shipment] Form not found in delivery modal');
            const errorEl = overlay.querySelector('[data-role="delivery-error"]');
            if (errorEl) {
                errorEl.textContent = 'Form not found. Please refresh and try again.';
                errorEl.style.display = 'block';
            }
            return;
        }

        const formData = new FormData(form);
        const address1 = String(formData.get('address1') || '').trim();
        const address2 = String(formData.get('address2') || '').trim();
        const city = String(formData.get('city') || '').trim();
        const addressState = String(formData.get('state') || '').trim();
        const zip = String(formData.get('zip') || '').trim();
        const deliveryTime = String(formData.get('deliveryTime') || '').trim();
        const deliveryInstructions = String(formData.get('deliveryInstructions') || '').trim();

        const errorEl = overlay.querySelector('[data-role="delivery-error"]');
        const saveBtn = overlay.querySelector('[data-action="et-save-delivery-address"]');
        if (errorEl) errorEl.style.display = 'none';

        const fieldsToValidate = [
            { name: 'address1', value: address1 },
            { name: 'address2', value: address2 },
            { name: 'city', value: city },
            { name: 'state', value: addressState },
            { name: 'zip', value: zip },
            { name: 'deliveryTime', value: deliveryTime },
            { name: 'deliveryInstructions', value: deliveryInstructions },
        ];
        let firstInvalidField = null;
        let firstInvalidMessage = '';
        for (const { name, value } of fieldsToValidate) {
            const result = validateDeliveryField(name, value);
            if (!result.valid) {
                firstInvalidField = name;
                firstInvalidMessage = result.message;
                break;
            }
        }

        if (firstInvalidField) {
            const fieldWrap = overlay.querySelector(`.et-shipment-modal-field[data-field="${firstInvalidField}"]`);
            const fieldErrorSpan = overlay.querySelector(`[data-field-error="${firstInvalidField}"]`);
            if (fieldWrap) fieldWrap.classList.add('et-shipment-modal-field--error');
            if (fieldErrorSpan) fieldErrorSpan.textContent = firstInvalidMessage;
            fieldsToValidate.forEach(({ name }) => {
                if (name !== firstInvalidField) {
                    const w = overlay.querySelector(`.et-shipment-modal-field[data-field="${name}"]`);
                    const s = overlay.querySelector(`[data-field-error="${name}"]`);
                    if (w) w.classList.remove('et-shipment-modal-field--error');
                    if (s) s.textContent = '';
                }
            });
            const focusId = firstInvalidField === 'deliveryInstructions' ? 'delivery-instructions' : (firstInvalidField === 'deliveryTime' ? 'delivery-time' : `delivery-${firstInvalidField}`);
            const focusEl = overlay.querySelector(`#${focusId}`);
            if (focusEl) focusEl.focus();
            if (saveBtn) {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
            return;
        }

        // Every pre-save failure below unwinds the same way: drop the spinner,
        // surface the reason in the modal banner, and hand the Save button back.
        const failBeforeSave = (message) => {
            setDeliveryModalSaveInProgress(overlay, false);
            if (errorEl) {
                errorEl.textContent = message;
                errorEl.style.display = 'block';
            }
            if (saveBtn) {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        };

        setDeliveryModalSaveInProgress(overlay, true);
        if (saveBtn) {
            saveBtn.disabled = true;
            saveBtn.textContent = 'Validating...';
        }

        try {
            const apiBaseUrl = getApiBaseUrl();
            const availabilityResp = await fetch(`${apiBaseUrl}/search/availability/${encodeURIComponent(zip)}`);
            if (!availabilityResp.ok) {
                throw new Error('Unable to validate address');
            }

            const availabilityData = await availabilityResp.json();
            const deliveryLocationId = availabilityData?.deliveryLocation?.location_id;
            const deliveryLocationName = availabilityData?.deliveryLocation?.name;

            if (!deliveryLocationId) {
                failBeforeSave(OUT_OF_DELIVERY_AREA_MESSAGE);
                return;
            }

            // `address1` is required by validateDeliveryField and the gate above
            // returns before this point, so the street line is always present --
            // which is what lets the payload send it unconditionally instead of
            // falling back to [''] and asking a courier about a blank address.
            const streetAddress = [address1];
            if (address2) streetAddress.push(address2);

            // Keep transport and parse failures inside this check instead of
            // letting them reach the generic catch, which renders `error.message`
            // verbatim -- an edge 502 returns an HTML body, so the subscriber
            // would read "Unexpected token '<'" (or "Failed to fetch" on a
            // dropped connection) in the banner and a toast. Both are "could not
            // ask": leaving `deliverabilityData` null falls through the verdict
            // check below to the retry copy. Not reported to Sentry -- a dropped
            // connection or an edge blip is the subscriber's network, not a defect.
            let deliverabilityData = null;
            let deliverabilityStatus = 0;
            try {
                const deliverabilityResp = await fetch(`${apiBaseUrl}/uber/check-deliverable`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        is_b2b: false,
                        location_id: Number(deliveryLocationId),
                        delivery_address: JSON.stringify({
                            street_address: streetAddress,
                            city: city,
                            state: addressState,
                            zip_code: zip,
                            country: 'US',
                        }),
                    }),
                });
                deliverabilityStatus = deliverabilityResp.status;
                deliverabilityData = await deliverabilityResp.json();
            } catch (checkError) {
                console.error('[manage-shipment] Deliverability check could not be completed:', checkError);
            }

            // A failed check is a 400 that still carries a verdict, so status alone
            // cannot tell "no courier" from "could not ask". The route omits
            // `is_deliverable` when it never reached a verdict -- a rejected
            // payload, a missing location, an unhandled 5xx -- and reading that as
            // "not deliverable" would blame the radius for it. Both spellings are
            // accepted here to match the deliverable check just below.
            const hasVerdict = deliverabilityData &&
                typeof deliverabilityData === 'object' &&
                ('is_deliverable' in deliverabilityData || 'deliverable' in deliverabilityData);

            if (!hasVerdict) {
                reportDeliverabilityAnomaly(
                    'Deliverability check returned no verdict (status ' + deliverabilityStatus + ')',
                    deliverabilityData
                );
                failBeforeSave(DELIVERABILITY_UNAVAILABLE_MESSAGE);
                return;
            }

            const isDeliverable = Boolean(
                deliverabilityData?.is_deliverable === true ||
                deliverabilityData?.deliverable === true
            );

            if (!isDeliverable) {
                failBeforeSave(getDeliverabilityBlockMessage(
                    deliverabilityData.code,
                    deliverabilityData.error
                ));
                return;
            }

            if (saveBtn) {
                saveBtn.textContent = 'Saving...';
            }

            await saveDeliveryAddressUpdate(subscriptionId, {
                address1,
                address2,
                city,
                state: addressState,
                zip,
                locationId: String(deliveryLocationId),
                deliveryTime,
                deliveryInstructions,
                deliveryLocationName,
            });

            if (overlay) {
                overlay.remove();
            }
            state.modalOpen = false;
            state.modalType = null;

            showSuccessToast('Delivery address updated successfully.');

            // Delay refresh to allow Recharge API to propagate the update
            setTimeout(() => {
                document.dispatchEvent(new CustomEvent('Affinity:refresh'));
            }, 3000);
        } catch (error) {
            console.error('[manage-shipment] Failed to save delivery address:', error);

            const fieldErrors = error?.fieldErrors && typeof error.fieldErrors === 'object' ? error.fieldErrors : null;
            const overlayEl = document.querySelector('[data-et-shipment-modal="true"]');
            const errorElLive = overlayEl?.querySelector('[data-role="delivery-error"]');

            if (fieldErrors && Object.keys(fieldErrors).length > 0 && overlayEl) {
                const deliveryFieldNames = ['address1', 'address2', 'city', 'state', 'zip', 'deliveryInstructions'];
                deliveryFieldNames.forEach((name) => {
                    const msg = fieldErrors[name] || '';
                    const fieldWrap = overlayEl.querySelector(`.et-shipment-modal-field[data-field="${name}"]`);
                    const errorSpan = overlayEl.querySelector(`[data-field-error="${name}"]`);
                    if (fieldWrap) fieldWrap.classList.toggle('et-shipment-modal-field--error', !!msg);
                    if (errorSpan) errorSpan.textContent = msg;
                });
                if (errorElLive) {
                    errorElLive.textContent = error.message || 'Please correct the errors above.';
                    errorElLive.style.display = 'block';
                }
            } else {
                if (errorElLive) {
                    errorElLive.textContent = error?.message || 'Unable to save address. Please try again.';
                    errorElLive.style.display = 'block';
                }
                showErrorToast(error?.message || 'Unable to save address. Please try again.');
            }

            const overlayToReenable = document.querySelector('[data-et-shipment-modal="true"]');
            if (overlayToReenable) setDeliveryModalSaveInProgress(overlayToReenable, false);
            if (saveBtn) {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        }
    }

    async function handleSavePickupLocation(subscriptionId, address) {
        const overlay = document.querySelector('[data-et-shipment-modal="true"]');
        if (!overlay) {
            throw new Error('Modal not found. Please refresh and try again.');
        }

        const parsedSubscriptionId = parseOptionalSubscriptionId(subscriptionId);
        if (!parsedSubscriptionId) {
            throw new Error('Invalid subscription ID. Please refresh the page.');
        }

        let locationId = overlay.dataset.selectedLocationId || '';
        let locationName = overlay.dataset.selectedLocationName || '';

        if (!locationId) {
            const allRadios = Array.from(document.querySelectorAll('input[name="pickup-location"]'));
            let selectedRadio = null;

            for (const radio of allRadios) {
                if (radio.checked) {
                    selectedRadio = radio;
                    break;
                }
            }

            if (!selectedRadio && allRadios.length > 0) {
                selectedRadio = allRadios[0];
                selectedRadio.checked = true;
            }

            if (selectedRadio) {
                locationId = String(selectedRadio.value || '').trim();
                locationName = String(selectedRadio.getAttribute('data-location-name') || '').trim();

                if (locationId) {
                    overlay.dataset.selectedLocationId = locationId;
                    overlay.dataset.selectedLocationName = locationName;
                }
            }
        }

        if (!locationId) {
            throw new Error('No pickup location selected. Please select a location and try again.');
        }

        try {
            await savePickupLocationUpdate(parsedSubscriptionId, locationId, locationName);

            overlay.remove();
            state.modalOpen = false;
            state.modalType = null;

            showSuccessToast('Pickup location updated successfully.');

            // Delay refresh to allow Recharge API to propagate the update
            setTimeout(() => {
                document.dispatchEvent(new CustomEvent('Affinity:refresh'));
            }, 3000);
        } catch (error) {
            console.error('[manage-shipment] Failed to save pickup location:', error);

            const errorEl = overlay.querySelector('[data-role="pickup-error"]');
            let errorShown = false;

            if (errorEl) {
                errorEl.textContent = error?.message || 'Unable to save pickup location. Please try again.';
                errorEl.style.display = 'block';
                errorShown = true;
            }

            if (!errorShown) {
                showErrorToast(error?.message || 'Unable to save pickup location. Please try again.');
            }

            const saveBtn = overlay.querySelector('[data-action="et-save-pickup-location"]');
            if (saveBtn) {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        }
    }

    function normalizeOrderAttributes(attributes) {
        if (!attributes) return [];
        const output = [];
        const items = Array.isArray(attributes) ? attributes : Object.entries(attributes);

        for (const item of items) {
            if (!item) continue;
            let name = '';
            let value = '';

            if (Array.isArray(item)) {
                name = String(item[0] || '').trim();
                value = String(item[1] ?? '').trim();
            } else if (typeof item === 'object') {
                name = String(item.name || item.key || Object.keys(item)[0] || '').trim();
                value = String(item.value != null ? item.value : (item[name] ?? '')).trim();
            }

            if (name) {
                output.push({ name, value });
            }
        }
        return output;
    }

    async function updateAddressWithRetry(session, addressId, addressData, maxAttempts = 3) {
        let lastError = null;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response = await updateAddress(session, addressId, addressData);
                return response;
            } catch (error) {
                lastError = error;

                // Check if it's a conflict error (409) that we should retry
                const status = Number(error?.status || error?.statusCode || error?.code);
                const isConflict = status === 409 || /409/.test(String(error?.message || ''));

                if (!isConflict || attempt >= maxAttempts) {
                    throw error;
                }

                console.warn(`[manage-shipment] Address update conflict (attempt ${attempt}), retrying...`);
                await new Promise(resolve => setTimeout(resolve, 250 * attempt));
            }
        }

        throw lastError || new Error('Failed to update address after retries');
    }

    function updateFulfillmentDateInAttributes(orderAttributes, subscription, timeStr) {
        if (!orderAttributes || !Array.isArray(orderAttributes)) return orderAttributes;

        const existingFulfillmentDate = readNamedAttributeValue(orderAttributes, 'Fulfillment Date');
        const datePart = existingFulfillmentDate ? extractIsoDatePart(existingFulfillmentDate) : resolveFallbackFulfillmentDatePart(subscription);

        const fulfillmentDate = buildFulfillmentDateValue(datePart, existingFulfillmentDate, timeStr);

        if (fulfillmentDate) {
            const idx = orderAttributes.findIndex((attr) => {
                return normalizeCustomAttributeKey(attr.name) === 'fulfillmentdate';
            });
            if (idx >= 0) {
                orderAttributes[idx].value = fulfillmentDate;
            } else {
                orderAttributes.push({ name: 'Fulfillment Date', value: fulfillmentDate });
            }
        }

        return orderAttributes;
    }

    async function saveDeliveryAddressUpdate(subscriptionId, addressData) {
        const session = await getPortalSession();
        const subscription = await getSubscriptionById(session, subscriptionId);
        const addressId = resolveSubscriptionAddressId(subscription);

        if (!addressId) {
            throw new Error('No address found for subscription');
        }

        const currentAddress = await resolveSubscriptionAddress(session, subscription);
        const currentDeliveryLocationId = extractDeliveryLocationIdFromOrderAttributes(currentAddress?.order_attributes);
        const currentFulfillmentType = extractFulfillmentTypeFromAddress(currentAddress);

        const locationIdToSet =
            addressData.locationId != null ? String(addressData.locationId).trim() : null;

        const updatedOrderAttributes = normalizeOrderAttributes(currentAddress?.order_attributes);

        const fulfillmentTypeIndex = updatedOrderAttributes.findIndex((attr) => {
            return normalizeCustomAttributeKey(attr.name) === 'fulfillmenttype';
        });

        if (fulfillmentTypeIndex >= 0) {
            updatedOrderAttributes[fulfillmentTypeIndex].value = 'delivery';
        } else {
            updatedOrderAttributes.push({ name: 'Fulfillment Type', value: 'delivery' });
        }

        if (locationIdToSet) {
            const locationIndex = updatedOrderAttributes.findIndex((attr) => {
                return normalizeCustomAttributeKey(attr.name) === 'locationid';
            });

            if (locationIndex >= 0) {
                updatedOrderAttributes[locationIndex].name = 'LocationID';
                updatedOrderAttributes[locationIndex].value = locationIdToSet;
            } else {
                updatedOrderAttributes.push({
                    name: 'LocationID',
                    value: locationIdToSet,
                });
            }
        }

        const instructionsIndex = updatedOrderAttributes.findIndex((attr) => {
            return normalizeCustomAttributeKey(attr.name) === 'deliveryinstructions';
        });

        if (addressData.deliveryInstructions) {
            if (instructionsIndex >= 0) {
                updatedOrderAttributes[instructionsIndex].value = addressData.deliveryInstructions;
            } else {
                updatedOrderAttributes.push({ name: 'Delivery Instructions', value: addressData.deliveryInstructions });
            }
        } else if (instructionsIndex >= 0) {
            updatedOrderAttributes.splice(instructionsIndex, 1);
        }

        const deliveryTimeInput = addressData.deliveryTime || '';
        updateFulfillmentDateInAttributes(updatedOrderAttributes, subscription, deliveryTimeInput || '12:00');

        const normalizedCurrent = currentDeliveryLocationId ? String(currentDeliveryLocationId).trim() : null;
        const normalizedNew = locationIdToSet || null;
        const deliveryLocationChanged = normalizedCurrent !== normalizedNew;
        const fulfillmentTypeChanged = currentFulfillmentType !== 'delivery';

        if (deliveryLocationChanged || fulfillmentTypeChanged) {
            const locationIdForBundle = locationIdToSet;
            if (locationIdForBundle) {
                await updateBundleSelectionsForLocation(session, subscriptionId, subscription, locationIdForBundle, null, 'delivery');
            }
        }

        await updateAddressWithRetry(session, addressId, {
            address1: addressData.address1,
            address2: addressData.address2 || '',
            city: addressData.city,
            province: addressData.state,
            zip: addressData.zip,
            order_attributes: updatedOrderAttributes,
        });
    }

    function extractFulfillmentTypeFromAddress(address) {
        if (!address || typeof address !== 'object') return 'delivery';

        const orderAttributes = address.order_attributes || address.orderAttributes || [];
        for (const attr of orderAttributes) {
            if (!attr || typeof attr !== 'object') continue;

            let key = null;
            let value = null;

            if ('name' in attr && 'value' in attr) {
                key = String(attr.name || '').trim();
                value = String(attr.value || '').trim();
            } else {
                const keys = Object.keys(attr);
                if (keys.length > 0) {
                    key = keys[0];
                    value = String(attr[key] || '').trim();
                }
            }

            if (key && normalizeCustomAttributeKey(key) === 'fulfillmenttype') {
                return value && String(value).trim().toLowerCase() === 'pickup' ? 'pickup' : 'delivery';
            }
        }

        return 'delivery';
    }

    async function savePickupLocationUpdate(subscriptionId, locationId, locationName) {
        if (state.saveInProgress) {
            return;
        }

        state.saveInProgress = true;

        try {
            const parsedSubscriptionId = parseOptionalSubscriptionId(subscriptionId);
            if (!parsedSubscriptionId) {
                throw new Error('Invalid subscription ID');
            }

            const session = await getPortalSession();
            const subscription = await getSubscriptionById(session, parsedSubscriptionId);
            const addressId = resolveSubscriptionAddressId(subscription);

            if (!addressId) {
                throw new Error('No address found for subscription');
            }

            const address = await resolveSubscriptionAddress(session, subscription);
            const currentLocationId = extractPickupLocationIdFromAddress(address);
            const currentFulfillmentType = extractFulfillmentTypeFromAddress(address);

            const normalizedCurrentLocationId = currentLocationId ? String(currentLocationId).trim() : null;
            const normalizedNewLocationId = locationId ? String(locationId).trim() : null;
            const locationChanged = normalizedCurrentLocationId !== normalizedNewLocationId;
            const fulfillmentTypeChanged = currentFulfillmentType !== 'pickup';

            if (locationChanged || fulfillmentTypeChanged) {
                await updateBundleSelectionsForLocation(session, parsedSubscriptionId, subscription, locationId, locationName);
            }

            const orderAttributesArr = normalizeOrderAttributes(address?.order_attributes);

            const fulfillmentTypeIndex = orderAttributesArr.findIndex(
                attr => normalizeCustomAttributeKey(attr.name) === 'fulfillmenttype'
            );
            if (fulfillmentTypeIndex !== -1) {
                orderAttributesArr[fulfillmentTypeIndex].value = 'pickup';
            } else {
                orderAttributesArr.push({ name: 'Fulfillment Type', value: 'pickup' });
            }

            const locationIdIndex = orderAttributesArr.findIndex(
                attr => normalizeCustomAttributeKey(attr.name) === 'locationid'
            );
            if (locationIdIndex !== -1) {
                orderAttributesArr[locationIdIndex].value = locationId;
            } else {
                orderAttributesArr.push({
                    name: 'LocationID',
                    value: locationId,
                });
            }

            // update Fulfillment Date with midnight time for pickup
            updateFulfillmentDateInAttributes(orderAttributesArr, subscription, '00:00');

            await updateAddressWithRetry(session, addressId, {
                order_attributes: orderAttributesArr,
            });
        } catch (error) {
            throw error;
        } finally {
            state.saveInProgress = false;
        }
    }

    async function fetchSettings() {
        const apiBaseUrl = getApiBaseUrl();
        try {
            const response = await fetch(`${apiBaseUrl}/settings`);
            if (!response.ok) {
                return null;
            }
            const settings = await response.json();
            const settingsMap = {};
            if (Array.isArray(settings)) {
                settings.forEach(setting => {
                    if (setting.key && setting.value) {
                        settingsMap[setting.key] = setting.value;
                    }
                });
            }
            return settingsMap;
        } catch (error) {
            return null;
        }
    }

    function resolveFeeConfig(apiSettings) {
        const themeConfig = window.etUpdateMealsConfig || {};
        const gridSettings = window.rechargeGridSettings || {};
        const bundleConf = window.bundleConfig || {};

        const resolve = (...candidates) => {
            for (const val of candidates) {
                const s = val != null ? String(val).trim() : '';
                if (s !== '') return s;
            }
            return '';
        };

        return {
            deliveryFeeProductId: resolve(
                themeConfig.deliveryFeeProductId,
                gridSettings.delivery_fee_product_id,
                bundleConf.deliveryFeeProductId,
                apiSettings?.delivery_fee_product_id
            ),
            deliveryFeeVariantId: resolve(
                themeConfig.deliveryFeeVariantId,
                gridSettings.delivery_fee_variant_id,
                bundleConf.deliveryFeeVariantId,
                apiSettings?.delivery_fee_variant_id
            ),
            packagingFeeProductId: resolve(
                themeConfig.packagingFeeProductId,
                gridSettings.packaging_fee_product_id,
                bundleConf.packagingFeeProductId,
                apiSettings?.packaging_fee_product_id
            ),
            packagingFeeVariantId: resolve(
                themeConfig.packagingFeeVariantId,
                gridSettings.packaging_fee_variant_id,
                bundleConf.packagingFeeVariantId,
                apiSettings?.packaging_fee_variant_id
            ),
            collectionId: resolve(
                themeConfig.feesCollectionId,
                gridSettings.fees_collection_id,
                gridSettings.collection_id,
                bundleConf.collectionId,
                apiSettings?.fees_collection_id,
                apiSettings?.collection_id
            ),
            subscriptionDeliveryFeeThresholdCents: Math.round(
                parseFloat(
                    resolve(
                        themeConfig.subscriptionDeliveryFeeThreshold,
                        apiSettings?.subscription_delivery_fee_waiver_threshold,
                        '50'
                    )
                ) * 100
            ),
        };
    }

    async function updateBundleSelectionsForLocation(session, subscriptionId, subscription, locationId, locationName = null, fulfillmentType = null) {
        const apiBaseUrl = getApiBaseUrl();

        try {
            const settings = await fetchSettings();
            const feeConfig = resolveFeeConfig(settings);
            const deliveryFeeProductId = feeConfig.deliveryFeeProductId;
            const packagingFeeProductId = feeConfig.packagingFeeProductId;

            let currentItems = Array.isArray(subscription?.include?.bundle_selections?.items)
                ? subscription.include.bundle_selections.items
                : [];

            if (currentItems.length === 0) {
                const bundleSelectionId = await resolveBundleSelectionId(session, subscription);
                if (bundleSelectionId) {
                    const bundleSelection = await getBundleSelectionById(session, bundleSelectionId);
                    currentItems = extractItemsFromBundleSelection(bundleSelection);
                }
            }
            if (currentItems.length === 0) {
                return;
            }

            const itemsWithoutFees = currentItems.filter(item => {
                const productId = String(item?.external_product_id || item?.product_id || '').trim();
                return productId !== deliveryFeeProductId && productId !== packagingFeeProductId;
            });

            const itemsToUpdate = itemsWithoutFees.length > 0 ? itemsWithoutFees : currentItems;

            const priceByItemId = new Map();
            for (const item of currentItems) {
                const id = item?.id || item?.external_product_id;
                if (!id) continue;
                const price = parseFloat(item?.price ?? item?.unit_price ?? 0);
                if (Number.isFinite(price) && price > 0) {
                    priceByItemId.set(String(id), price);
                }
            }

            const itemsToUpdateWithPrices = itemsToUpdate.map(item => {
                const id = item?.id || item?.external_product_id;
                const price = priceByItemId.get(String(id));
                if (price && !item.price && !item.unit_price) {
                    return { ...item, price: price, unit_price: price };
                }
                return item;
            });

            let updatedItems = itemsToUpdateWithPrices;
            let mealSubtotalCentsForDeliveryFee = 0;
            let priceByVariantIdFromProductSearch = new Map();

            if (locationId) {
                const normalizedLocationId = String(locationId).trim();

                let finalLocationName = locationName;
                if (!finalLocationName) {
                    const locationResp = await fetch(`${apiBaseUrl}/search/availability/location/${encodeURIComponent(normalizedLocationId)}`);
                    if (locationResp.ok) {
                        const locationData = await locationResp.json();
                        finalLocationName = locationData?.pickupLocations?.find(loc =>
                            String(loc.location_id || loc.id) === normalizedLocationId
                        )?.name || locationData?.deliveryLocation?.name;
                    }
                }

                if (finalLocationName) {
                    const catalogResp = await fetch(
                        `${apiBaseUrl}/location/catalog/${encodeURIComponent(normalizedLocationId)}/${encodeURIComponent(finalLocationName)}`
                    );

                    if (catalogResp.ok) {
                        const catalogData = await catalogResp.json();
                        const catalogId = catalogData?.catalogId;

                        if (catalogId) {
                            const normalizedCatalogId = String(catalogId).replace('gid://shopify/MarketCatalog/', '');
                            const variantsResp = await fetch(`${apiBaseUrl}/subscriptions/${normalizedCatalogId}/variants`);

                            if (variantsResp.ok) {
                                const variantsData = await variantsResp.json();
                                const variants = Array.isArray(variantsData?.variants) ? variantsData.variants : [];

                                if (variants.length > 0) {
                                    const catalogIdStr = String(normalizedCatalogId).trim();
                                    const variantsMatchingCatalog = variants.filter(v => {
                                        const val = v?.metafield?.value;
                                        if (val == null) return false;
                                        return String(val).trim() === catalogIdStr;
                                    });


                                    const variantIdsForSearch = variantsMatchingCatalog
                                        .map(v => {
                                            const id = v?.id || v?.external_variant_id;
                                            if (!id) return null;
                                            const str = String(id);
                                            const numericId = str.replace('gid://shopify/ProductVariant/', '').trim() || str.split('/').pop();
                                            return numericId || null;
                                        })
                                        .filter(Boolean);

                                    const productToRechargeVariantIdMap = new Map();
                                    const hasProductSearch = Boolean(getOptionalRechargeMethod(['product', 'productSearch']));
                                    const PRODUCT_SEARCH_CHUNK_SIZE = 100;
                                    const variantIdsForSearchSet = new Set(variantIdsForSearch);

                                    if (variantIdsForSearch.length > 0 && hasProductSearch) {
                                        const chunks = [];
                                        for (let i = 0; i < variantIdsForSearch.length; i += PRODUCT_SEARCH_CHUNK_SIZE) {
                                            chunks.push(variantIdsForSearch.slice(i, i + PRODUCT_SEARCH_CHUNK_SIZE));
                                        }
                                        const runChunk = (chunk) =>
                                            productSearch(session, {
                                                format_version: '2022-06',
                                                external_variant_ids: chunk,
                                                include_deleted: false,
                                                exclude_hidden: false,
                                            });
                                        const results = await Promise.allSettled(chunks.map(runChunk));
                                        const fulfilledResponses = results
                                            .filter((r) => r.status === 'fulfilled')
                                            .map((r) => r.value);
                                        const rejectedIndices = results
                                            .map((r, idx) => (r.status === 'rejected' ? idx : -1))
                                            .filter((idx) => idx >= 0);
                                        if (rejectedIndices.length > 0) {
                                            for (const idx of rejectedIndices) {
                                                try {
                                                    const retryResponse = await runChunk(chunks[idx]);
                                                    fulfilledResponses.push(retryResponse);
                                                } catch (retryErr) {
                                                    console.warn('[manage-shipment] productSearch chunk retry failed:', idx, retryErr);
                                                }
                                            }
                                        }
                                        for (const productSearchResponse of fulfilledResponses) {
                                            const rechargeProducts = Array.isArray(productSearchResponse?.products) ? productSearchResponse.products : [];
                                            for (const product of rechargeProducts) {
                                                const productIdRaw = product?.external_product_id || product?.id || '';
                                                const normalizedProductId = String(productIdRaw).replace('gid://shopify/Product/', '').trim();
                                                const productVariants = Array.isArray(product?.variants) ? product.variants : [];
                                                for (const variant of productVariants) {
                                                    const variantId = String(variant?.external_variant_id || '').trim();
                                                    if (!variantId) continue;
                                                    const normalizedVariantId = variantId.replace('gid://shopify/ProductVariant/', '').trim();
                                                    if (variantIdsForSearchSet.has(normalizedVariantId) && normalizedProductId) {
                                                        productToRechargeVariantIdMap.set(normalizedProductId, variant.external_variant_id || variantId);
                                                        let priceValue = null;
                                                        const prices = Array.isArray(variant?.prices) ? variant.prices : [];
                                                        const firstPrice = prices[0];
                                                        const plans = Array.isArray(firstPrice?.plans) ? firstPrice.plans : [];
                                                        const firstPlan = plans[0];
                                                        const discountedPrice = firstPlan?.discounted_price;
                                                        if (discountedPrice != null) {
                                                            priceValue = parseFloat(discountedPrice);
                                                        } else {
                                                            // Fallback to unit price from variant
                                                            const unitPrice = variant?.price ?? firstPrice?.unit_price ?? firstPlan?.price;
                                                            if (unitPrice != null) {
                                                                priceValue = parseFloat(unitPrice);
                                                            }
                                                        }
                                                        if (Number.isFinite(priceValue) && priceValue >= 0) {
                                                            priceByVariantIdFromProductSearch.set(normalizedVariantId, priceValue);
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    updatedItems = itemsToUpdateWithPrices.map(currentItem => {
                                        const productIdRaw = currentItem?.external_product_id || '';
                                        const normalizedProductId = String(productIdRaw).replace('gid://shopify/Product/', '').trim();
                                        const newVariantId = normalizedProductId ? productToRechargeVariantIdMap.get(normalizedProductId) : null;
                                        return {
                                            id: currentItem.id,
                                            collection_id: currentItem.collection_id,
                                            collection_source: currentItem.collection_source,
                                            created_at: currentItem.created_at,
                                            external_product_id: currentItem.external_product_id,
                                            external_variant_id: newVariantId || currentItem.external_variant_id,
                                            quantity: currentItem.quantity || 1,
                                            updated_at: currentItem.updated_at,
                                            price: currentItem.price,
                                            unit_price: currentItem.unit_price,
                                        };
                                    });

                                    mealSubtotalCentsForDeliveryFee = updatedItems.reduce((sum, it) => {
                                        const vid = String(it.external_variant_id || '').replace('gid://shopify/ProductVariant/', '').trim();
                                        const productSearchPrice = priceByVariantIdFromProductSearch.get(vid);
                                        const qty = parseInteger(it.quantity, 1);
                                        let priceCents = 0;
                                        if (Number.isFinite(productSearchPrice)) {
                                            priceCents = Math.round(productSearchPrice * 100) * qty;
                                        } else {
                                            const fallbackPrice = parseFloat(it.price ?? it.unit_price ?? 0);
                                            priceCents = Math.round((Number.isFinite(fallbackPrice) ? fallbackPrice : 0) * 100) * qty;
                                        }
                                        return sum + priceCents;
                                    }, 0);
                                }
                            }
                        }
                    }
                }
            }

            const targetFulfillmentType = (fulfillmentType || (locationName != null ? 'pickup' : 'delivery')).toLowerCase();

            const bundleItems = updatedItems.map(item => ({
                id: item.id,
                updated_at: item.updated_at,
                collection_source: item.collection_source,
                collection_id: item.collection_id,
                external_product_id: item.external_product_id,
                external_variant_id: item.external_variant_id,
                quantity: parseInteger(item.quantity, 1),
            }));

            if (targetFulfillmentType === 'delivery') {
                if (mealSubtotalCentsForDeliveryFee === 0) {
                    mealSubtotalCentsForDeliveryFee = updatedItems.reduce((sum, it) => {
                        const vid = String(it.external_variant_id || '').replace('gid://shopify/ProductVariant/', '').trim();
                        const productSearchPrice = priceByVariantIdFromProductSearch.get(vid);
                        const qty = parseInteger(it.quantity, 1);
                        let priceCents = 0;
                        if (Number.isFinite(productSearchPrice)) {
                            priceCents = Math.round(productSearchPrice * 100) * qty;
                        } else {
                            const preservedPrice = parseFloat(it.price ?? it.unit_price ?? 0);
                            priceCents = Math.round((Number.isFinite(preservedPrice) ? preservedPrice : 0) * 100) * qty;
                        }
                        return sum + priceCents;
                    }, 0);
                }
                if (feeConfig.packagingFeeProductId && feeConfig.packagingFeeVariantId) {
                    bundleItems.push({
                        collection_id: feeConfig.collectionId || '',
                        external_product_id: feeConfig.packagingFeeProductId,
                        external_variant_id: feeConfig.packagingFeeVariantId,
                        quantity: 1,
                    });
                }
                if (feeConfig.deliveryFeeProductId && feeConfig.deliveryFeeVariantId) {
                    const shouldAddDeliveryFee = mealSubtotalCentsForDeliveryFee < feeConfig.subscriptionDeliveryFeeThresholdCents;
                    if (shouldAddDeliveryFee) {
                        bundleItems.push({
                            collection_id: feeConfig.collectionId || '',
                            external_product_id: feeConfig.deliveryFeeProductId,
                            external_variant_id: feeConfig.deliveryFeeVariantId,
                            quantity: 1,
                        });
                    }
                }
            }

            const bundleSelectionId = await resolveBundleSelectionId(session, subscription);
            if (!bundleSelectionId) {
                throw new Error('Unable to resolve bundle selection ID');
            }

            const bundlePayload = {
                items: bundleItems,
                purchase_item_id: subscription?.purchase_item?.id || subscription?.purchaseItemId || null,
            };

            await updateBundleSelection(session, bundleSelectionId, bundlePayload);
        } catch (error) {
            console.error('[manage-shipment] Error updating bundle selections:', error);
            throw error;
        }
    }

    async function openDelivery(subscriptionId) {
        showLoaderOverlay();
        startDeliveryOpenSpan();
        // Every exit that gives up on the modal ends the open span with it.
        const failOpen = (message, outcome = 'error') => {
            endDeliveryOpenSpan({ 'et.portal.outcome': outcome });
            hideLoaderOverlay();
            showErrorToast(message);
        };

        const parsedId = parseOptionalSubscriptionId(subscriptionId);
        if (!parsedId) {
            failOpen('Invalid subscription ID. Please try again.');
            return;
        }
        if (!window.etRechargeSdk) {
            console.error('[manage-shipment] Recharge SDK not available');
            failOpen('Shipment management is not available. Please refresh the page.');
            return;
        }

        if (typeof window.etRechargeSdk.getMethod !== 'function') {
            console.error('[manage-shipment] Recharge SDK not properly initialized');
            failOpen('Shipment management is not available. Please refresh the page.');
            return;
        }

        if (state.modalOpen) {
            const existingOverlay = document.querySelector('[data-et-shipment-modal="true"]');
            if (existingOverlay) {
                existingOverlay.remove();
            }
            state.modalOpen = false;
            state.modalType = null;
            // Small delay to ensure cleanup completes
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        state.modalOpen = true;
        state.modalType = 'delivery';

        try {
            const session = await getPortalSession();
            const subscription = await getSubscriptionById(session, parsedId);
            const address = await resolveSubscriptionAddress(session, subscription);

            if (!address) {
                failOpen('Unable to load address information.', 'no-address');
                state.modalOpen = false;
                state.modalType = null;
                return;
            }

            const modalMarkup = buildDeliveryAddressModalMarkup(address, parsedId, subscription);
            const modalContainer = createModalContainer(modalMarkup);
            document.body.appendChild(modalContainer);

            bindDeliveryModalHandlers(modalContainer, parsedId, address, subscription);
            hideLoaderOverlay();
        } catch (error) {
            console.error('[manage-shipment] Failed to open delivery modal:', error);
            failOpen('Unable to open delivery management. Please try again.');
            state.modalOpen = false;
            state.modalType = null;
        }
    }

    async function openPickup(subscriptionId) {
        showLoaderOverlay();

        const parsedId = parseOptionalSubscriptionId(subscriptionId);
        if (!parsedId) {
            hideLoaderOverlay();
            showErrorToast('Invalid subscription ID. Please try again.');
            return;
        }

        if (!window.etRechargeSdk) {
            console.error('[manage-shipment] Recharge SDK not available');
            hideLoaderOverlay();
            showErrorToast('Shipment management is not available. Please refresh the page.');
            return;
        }

        if (typeof window.etRechargeSdk.getMethod !== 'function') {
            console.error('[manage-shipment] Recharge SDK not properly initialized');
            hideLoaderOverlay();
            showErrorToast('Shipment management is not available. Please refresh the page.');
            return;
        }

        if (state.modalOpen) {
            const existingOverlay = document.querySelector('[data-et-shipment-modal="true"]');
            if (existingOverlay) {
                existingOverlay.remove();
            }
            state.modalOpen = false;
            state.modalType = null;
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        state.modalOpen = true;
        state.modalType = 'pickup';

        try {
            const session = await getPortalSession();
            const subscription = await getSubscriptionById(session, parsedId);
            const address = await resolveSubscriptionAddress(session, subscription);

            if (!address) {
                hideLoaderOverlay();
                showErrorToast('Unable to load address information.');
                state.modalOpen = false;
                state.modalType = null;
                return;
            }

            const currentLocationId = extractPickupLocationIdFromAddress(address);

            const modalMarkup = buildPickupLocationModalMarkup(parsedId, currentLocationId);
            const modalContainer = createModalContainer(modalMarkup);
            document.body.appendChild(modalContainer);
            await bindPickupModalHandlers(modalContainer, parsedId, address, currentLocationId);
            hideLoaderOverlay();
        } catch (error) {
            console.error('[manage-shipment] Failed to open pickup modal:', error);
            hideLoaderOverlay();
            showErrorToast('Unable to open pickup management. Please try again.');
            state.modalOpen = false;
            state.modalType = null;
        }
    }

    window.etManageShipment = {
        openDelivery,
        openPickup,
    };
})();
